import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Grid,
    IconButton,
    CircularProgress,
    Autocomplete,
    TextField,
    useTheme,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Snackbar,
    Chip,
    Tooltip,
    Paper,
    Checkbox,
    Divider,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MedicationIcon from "@mui/icons-material/Medication";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QueryBuilderIcon from "@mui/icons-material/QueryBuilder";
import WarningIcon from "@mui/icons-material/Warning";
import BlockIcon from "@mui/icons-material/Block";
import InfoIcon from "@mui/icons-material/Info";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import CalendarIcon from "@mui/icons-material/CalendarMonth";
import TimeIcon from "@mui/icons-material/AccessTime";
import PharmacyIcon from "@mui/icons-material/LocalPharmacy";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";
import InventoryIcon from "@mui/icons-material/Inventory";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import { useAuth } from "../contexts/AuthContext";
import {
    format,
    addDays,
    subDays,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameDay,
    startOfMonth,
    endOfMonth,
    isSameMonth,
    addMonths,
    subMonths,
    isAfter,
    isBefore,
    startOfDay,
    parse,
    parseISO
} from "date-fns";
import residentService from "../services/residentService";
import orderService from "../services/OrderMedicationService";
import clinicalNoteService from "../services/clinicalNoteService";
import apiClient from "../services/apiClient";
import masterDataService from "../services/masterDataService";
import userService from "../services/userService";
import { UserDTO } from "../types/user.types";
import { ResidentDTO } from "../types/resident.types";
import { MedicationOrderDTO, MedicationRoute, CreateMedicationOrderRequest } from "../types/medication.types";
import { useTenant } from "../contexts/TenantContext";
import { useLocation } from "react-router-dom";
import MedicationAdministrationRecord from "../components/MedicationAdministrationRecord";
import SignatureInput from "../components/SignatureInput";


const COLORS = {
    primary: "#0077B6",
    primaryLight: "#E0F2FE",
    surface: "#FFFFFF",
    background: "#F4F6F9",
    border: "#E2E8F0",
    textPrimary: "#1A2B45",
    textSecondary: "#5C6F85",
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; label: string }> = {
    Taken: { color: "#16A34A", bg: "#F0FDF4", label: "Taken" },
    Missed: { color: "#DC2626", bg: "#FEF2F2", label: "Missed" },
    "Not Given": { color: "#DC2626", bg: "#FEF2F2", label: "Not Given" },
    "Not Taken": { color: "#DC2626", bg: "#FEF2F2", label: "Not Given" },
    Upcoming: { color: "#7C3AED", bg: "#F5F3FF", label: "Upcoming" },
    Refused: { color: "#f06292", bg: "#fce4ec", label: "Refused" },
    "Out of Stock": { color: "#6B7280", bg: "#F9FAFB", label: "Out of Stock" },
    Discontinued: { color: "#78350F", bg: "#FEF3C7", label: "Discontinued" },
    PRN: { color: "#0077B6", bg: "#E0F2FE", label: "PRN" },
};

const STAT_REGISTRY_KEY = "qt-carex-stat-dose-medications";

const isStatDoseSource = (entity: any): boolean => {
    if (!entity || typeof entity !== "object") return false;

    const sourceLike = String(
        entity.source ??
        entity.medicationSource ??
        entity.orderSource ??
        entity.type ??
        entity.orderType ??
        entity.medicationType ??
        "",
    ).toLowerCase();

    const scheduleLike = String(
        entity.schedule ??
        entity.frequency ??
        entity.reason ??
        "",
    ).trim().toLowerCase();

    return Boolean(
        entity.isStatDose === true ||
        entity.statDose === true ||
        entity.isStat === true ||
        sourceLike === "stat_dose" ||
        sourceLike === "stat-dose" ||
        sourceLike === "statdose" ||
        scheduleLike === "stat" ||
        scheduleLike === "stat dose" ||
        scheduleLike === "stat_dose" ||
        scheduleLike === "stat-dose",
    );
};

const normalizeMedName = (value: any): string => String(value ?? "").trim().toLowerCase();

const StatusChip: React.FC<{ label: string; color: string; showStatIcon?: boolean; isNarcotic?: boolean; isCritical?: boolean; size?: "small" | "medium" }> = ({ label, color, showStatIcon = false, isNarcotic = false, isCritical = false, size = "small" }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
            label={label}
            size={size}
            sx={{
                height: size === "small" ? 18 : 22,
                fontSize: size === "small" ? "0.6rem" : "0.75rem",
                fontWeight: 700,
                bgcolor: `${color}18`,
                color,
                border: "none",
                "& .MuiChip-label": { px: 0.75 },
            }}
        />
        {showStatIcon && (
            <Tooltip title="STAT Medication" arrow>
                <Box
                    component="img"
                    src="https://cdn-icons-png.flaticon.com/512/3791/3791036.png"
                    alt="STAT dose"
                    sx={{
                        width: size === "small" ? 14 : 18,
                        height: size === "small" ? 14 : 18,
                        display: "block",
                        filter: "invert(31%) sepia(86%) saturate(1637%) hue-rotate(174deg) brightness(97%) contrast(101%)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                            transform: "scale(1.15)",
                        },
                    }}
                />
            </Tooltip>
        )}
        {isNarcotic && (
            <Tooltip title="Narcotic Medication" arrow>
                <Box
                    component="img"
                    src={process.env.PUBLIC_URL + "/assets/narcotic_icon.png"}
                    alt="Narcotic"
                    sx={{
                        width: size === "small" ? 14 : 18,
                        height: size === "small" ? 14 : 18,
                        display: "block",
                        objectFit: "contain",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                            transform: "scale(1.15)",
                        },
                    }}
                />
            </Tooltip>
        )}
        {isCritical && (
            <Tooltip title="Critical Medication" arrow>
                <Box
                    component="img"
                    src={process.env.PUBLIC_URL + "/assets/critical1.png"}
                    alt="Critical"
                    sx={{
                        width: size === "small" ? 24 : 28,
                        height: size === "small" ? 24 : 28,
                        display: "block",
                        objectFit: "contain",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                            transform: "scale(1.15)",
                        },
                    }}
                />
            </Tooltip>
        )}
    </Box>
);

// ─── Time Slots ────────────────────────────────────────────────────────────────
const TIME_SLOTS = [
    { label: "Morning", sub: "6 AM – 9 AM", start: 6, end: 9, icon: "🌅" },
    { label: "Mid-AM", sub: "9 AM – 12 PM", start: 9, end: 12, icon: "☀️" },
    { label: "Noon", sub: "12 PM – 3 PM", start: 12, end: 15, icon: "🌤️" },
    { label: "Afternoon", sub: "3 PM – 6 PM", start: 15, end: 18, icon: "🌇" },
    { label: "Evening", sub: "6 PM – 9 PM", start: 18, end: 21, icon: "🌆" },
    { label: "Night", sub: "9 PM – 12 AM", start: 21, end: 24, icon: "🌙" },
];

const getTimeFromSlot = (slot: string) => {
    switch (slot) {
        case "Morning": return "08:00";
        case "Mid-AM": return "10:30";
        case "Noon": return "13:00";
        case "Afternoon": return "15:00";
        case "Evening": return "18:00";
        case "Night": return "21:00";
        default: return undefined;
    }
};


const MedicationSchedulePage: React.FC = () => {
    const { user } = useAuth();
    const theme = useTheme();
    const location = useLocation();
    const { tenant } = useTenant();
    const [residents, setResidents] = useState<ResidentDTO[]>([]);
    const [selectedResident, setSelectedResident] = useState<ResidentDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // default to Day
    const [currentDate, setCurrentDate] = useState(new Date());
    const [medications, setMedications] = useState<any[]>([]);
    const [doseSummary, setDoseSummary] = useState<any | null>(null);
    const [activeOrders, setActiveOrders] = useState<MedicationOrderDTO[]>([]);

    // Status Update Dialog
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [selectedDateForUpdate, setSelectedDateForUpdate] = useState<Date | null>(null);
    const [selectedMed, setSelectedMed] = useState<any>(null);
    const [updateForm, setUpdateForm] = useState({
        medicationName: "",
        dosage: "",
        status: "",
        reason: "",
        results: "",
        lowStock: false,
        time: "09:00",
        updatedStock: "",
    });

    const [bulkUpdatePendingStatus, setBulkUpdatePendingStatus] = useState<string | null>(null);
    const [bulkUpdateSource, setBulkUpdateSource] = useState<"Missed" | "Refused" | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [schedules, setSchedules] = useState<any[]>([]);
    const [routes, setRoutes] = useState<any[]>([]);
    const [formError, setFormError] = useState<string | null>(null);

    const getStatRegistryContextKeys = (): string[] => {
        const residentId = selectedResident?.id;
        if (!residentId) return [];

        // Keep keys stable even if tenant info loads later.
        const keys: string[] = [];
        keys.push(`resident::${residentId}`);
        keys.push(`unknown::${residentId}`);
        if (tenant?.id) keys.push(`${tenant.id}::${residentId}`);

        // De-dupe
        return Array.from(new Set(keys));
    };

    const getStatRegistry = (): Record<string, string[]> => {
        try {
            const raw = localStorage.getItem(STAT_REGISTRY_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    };

    const getStatNamesForCurrentResident = (): Set<string> => {
        const registry = getStatRegistry();
        const keys = getStatRegistryContextKeys();
        const all = keys.flatMap(k => registry[k] || []);
        return new Set(all.map(normalizeMedName).filter(Boolean));
    };

    const registerStatMedicationName = (medicationName: string) => {
        const normalized = normalizeMedName(medicationName);
        if (!normalized) return;
        try {
            const registry = getStatRegistry();
            const keys = getStatRegistryContextKeys();
            // If selectedResident isn't ready, do nothing (can't scope it).
            if (keys.length === 0) return;

            keys.forEach((key) => {
                const existing = new Set((registry[key] || []).map(normalizeMedName).filter(Boolean));
                existing.add(normalized);
                registry[key] = Array.from(existing);
            });
            localStorage.setItem(STAT_REGISTRY_KEY, JSON.stringify(registry));
        } catch {
            // Ignore storage failures; UI will still rely on API flags.
        }
    };
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" | "info" | "warning" });

    const [isMissedDialogOpen, setIsMissedDialogOpen] = useState(false);
    const [missedDosesList, setMissedDosesList] = useState<any[]>([]);
    const [selectedMissedDoses, setSelectedMissedDoses] = useState<number[]>([]);

    const [isRefusedDialogOpen, setIsRefusedDialogOpen] = useState(false);
    const [marRefreshTrigger, setMarRefreshTrigger] = useState(0);
    const [refusedDosesList, setRefusedDosesList] = useState<any[]>([]);
    const [selectedRefusedDoses, setSelectedRefusedDoses] = useState<number[]>([]);

    const [isDayDetailsDialogOpen, setIsDayDetailsDialogOpen] = useState(false);
    const [selectedDayForDetails, setSelectedDayForDetails] = useState<Date | null>(null);
    const [dosesForSelectedDay, setDosesForSelectedDay] = useState<any[]>([]);

    const [isRuntimeMedDialogOpen, setIsRuntimeMedDialogOpen] = useState(false);
    const [isMarDialogOpen, setIsMarDialogOpen] = useState(false);
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const getLocalTimeStr = () => {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    };

    const [runtimeMedForm, setRuntimeMedForm] = useState<{
        medicationName: string;
        dosage: string;
        unit: string;
        route: string;
        startDate: Date | string | null;
        refillDate: Date | string | null;
        schedule: string;
        timeSlot: string;
        stockQuantity: string;
        note: string;
        isNarcotic: boolean;
        isCritical?: boolean;
        narcoticStorageLocation: string;
        givenTime: string;
    }>({
        medicationName: "",
        dosage: "",
        unit: "",
        route: "",
        startDate: new Date(),
        refillDate: "",
        schedule: "STAT Dose",
        timeSlot: "",
        stockQuantity: "1",
        note: "",
        isNarcotic: false,
        isCritical: false,
        narcoticStorageLocation: "",
        givenTime: getLocalTimeStr(),
    });

    const normalizedUserRole = (user?.role || "")
        .toString()
        .trim()
        .toUpperCase()
        .replace(/^ROLE_/, "");
    const isOnlyCaregiver = normalizedUserRole === "CAREGIVER" || normalizedUserRole === "NURSE";
    const [isSignatureInputOpen, setIsSignatureInputOpen] = useState(false);

    // Narcotic Verification Dialog states
    const [narcoticDialogOpen, setNarcoticDialogOpen] = useState(false);
    const [otherCaregivers, setOtherCaregivers] = useState<UserDTO[]>([]);
    const [caregiversLoading, setCaregiversLoading] = useState(false);
    const [selectedWitnessId, setSelectedWitnessId] = useState<string>('');
    const [witnessSignatureDialogOpen, setWitnessSignatureDialogOpen] = useState(false);
    const [witnessSignatureData, setWitnessSignatureData] = useState<string | null>(null);
    // For inline signature creation inside the narcotic dialog (admin caregiver)
    const [mySignatureDialogOpen, setMySignatureDialogOpen] = useState(false);
    const [adminSignatureData, setAdminSignatureData] = useState<string | null>(null);
    const [adminSignatureMode, setAdminSignatureMode] = useState<'upload' | 'digital' | null>(null);

    // 'upload' | 'digital' — which tab the witness is using
    const [witnessSignatureMode, setWitnessSignatureMode] = useState<'upload' | 'digital' | null>(null);

    const adminUploadRef = useRef<HTMLInputElement>(null);
    const witnessUploadRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, setSignatureData: (data: string) => void) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') {
                setSignatureData(reader.result);
            }
        };
        reader.readAsDataURL(file);
        event.target.value = ''; // clear input
    };

    // Load the logged-in user's saved profile signature from localStorage
    // Use state so it reacts after the user creates one inline inside this dialog
    const [myProfileSignature, setMyProfileSignature] = useState<string | null>(
        user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null
    );
    // Re-read when user id changes
    useEffect(() => {
        setMyProfileSignature(user?.id ? localStorage.getItem(`user_signature_${user.id}`) : null);
    }, [user?.id]);

    const dataURLtoFile = (dataurl: string, filename: string) => {
        const arr = dataurl.split(',');
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : '';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    useEffect(() => {
        if (formError) {
            const timer = setTimeout(() => {
                setFormError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [formError]);

    // Auto-calculate stock quantity for STAT Dose
    useEffect(() => {
        const { startDate, refillDate, schedule } = runtimeMedForm;
        if (startDate && refillDate && schedule) {
            const start = startOfDay(new Date(startDate));
            const end = startOfDay(new Date(refillDate));

            if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
                // difference in days (inclusive)
                const diffTime = end.getTime() - start.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

                let freq = 0;
                const s = schedule.toLowerCase();
                if (s.includes("once daily") || s.includes("bedtime") || s.includes("evening time")) freq = 1;
                else if (s.includes("twice daily")) freq = 2;
                else if (s.includes("three times daily")) freq = 3;
                else if (s.includes("four times daily")) freq = 4;
                else if (s.includes("every 4 hours")) freq = 6;
                else if (s.includes("every 6 hours")) freq = 4;
                else if (s.includes("every 8 hours")) freq = 3;
                else if (s.includes("every 12 hours")) freq = 2;

                if (freq > 0) {
                    const calculatedStock = diffDays * freq;
                    if (runtimeMedForm.stockQuantity !== String(calculatedStock)) {
                        setRuntimeMedForm(prev => ({ ...prev, stockQuantity: String(calculatedStock) }));
                    }
                }
            }
        }
    }, [runtimeMedForm.startDate, runtimeMedForm.refillDate, runtimeMedForm.schedule]);

    useEffect(() => {
        if (tenant?.id) {
            fetchResidents();
        }
    }, [tenant]);

    // Fetch other caregivers for the narcotic witness dropdown
    useEffect(() => {
        const facilityId = tenant?.id
            || (Array.isArray(user?.facility) ? user?.facility[0]?.id : (user?.facility as any)?.id)
            || user?.tenantId;

        if (!facilityId) return;

        const fetchCaregivers = async () => {
            setCaregiversLoading(true);
            try {
                const caregivers = await userService.getCaregiversByFacility(facilityId);
                // Exclude the currently logged-in user from the witness list.
                // Use OR-based exclusion: remove if keycloakId, id, OR username matches
                // the logged-in user — handles cases where keycloakId may be null from API.
                const isCurrentUser = (u: UserDTO): boolean => {
                    if (u.keycloakId && u.keycloakId === user?.id) return true;
                    if (u.id && u.id === user?.id) return true;
                    if (u.username && user?.username && u.username === user.username) return true;
                    return false;
                };
                setOtherCaregivers(caregivers.filter((u) => !isCurrentUser(u)));
            } catch (err) {
                console.error('Failed to fetch caregivers for facility', facilityId, err);
                // Fallback: fetch all active users if the facility endpoint fails
                try {
                    const users = await userService.getActiveUsers();
                    setOtherCaregivers(users.filter(u => {
                        if (u.keycloakId && u.keycloakId === user?.id) return false;
                        if (u.id && u.id === user?.id) return false;
                        if (u.username && user?.username && u.username === user.username) return false;
                        return true;
                    }));
                } catch (fallbackErr) {
                    console.error('Fallback caregiver fetch also failed', fallbackErr);
                }
            } finally {
                setCaregiversLoading(false);
            }
        };
        fetchCaregivers();
    }, [tenant?.id, user?.id, user?.tenantId]);

    const fetchSchedules = async () => {
        try {
            const scheduleData = await masterDataService.getActiveLookupValuesByName('Schedule');
            setSchedules(scheduleData);
        } catch (err) {
            console.error("Failed to fetch schedules", err);
            setSchedules([]);
        }
    };

    const fetchRoutes = async () => {
        try {
            const routeData = await masterDataService.getActiveLookupValuesByName('Route');
            setRoutes(routeData);
        } catch (err) {
            console.error("Failed to fetch routes", err);
            setRoutes([]);
        }
    };

    useEffect(() => {
        if (selectedResident?.id) {
            fetchMedicationData();
        } else {
            setMedications([]);
            setDoseSummary(null);
        }
    }, [selectedResident, currentDate, activeTab]);

    const fetchResidents = async () => {
        try {
            const data = await residentService.getActiveLookup(tenant!.id!);
            // Sort residents alphabetically by first name and last name
            const sorted = [...(data || [])].sort((a: any, b: any) => {
                const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
                const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
                return nameA.localeCompare(nameB);
            });
            setResidents(sorted);

            const locState = location.state as any;
            if (locState?.residentId) {
                const targetRes = sorted.find(r => r.id === locState.residentId);
                if (targetRes) {
                    setSelectedResident(targetRes);
                }
            }
        } catch (err) {
            console.error("Failed to fetch residents", err);
        }
    };

    const buildPatientDetails = (res: any) =>
        res
            ? {
                name: `${res.firstName} ${res.middleName ? res.middleName + " " : ""
                    }${res.lastName}`.trim(),
                EMRNo:
                    (res as any).medicalRecordNumber ||
                    (res as any).emrNumber ||
                    (res as any).medicareNumber ||
                    (res as any).medicaidNumber ||
                    res.id ||
                    "N/A",
                bp: (res as any).bloodPressure || (res as any).bp || "N/A",
                weight: (res as any).weight || "N/A",
                height: (res as any).height || "N/A",
                bmi: (() => {
                    if ((res as any).bmi) return (res as any).bmi;
                    const w = parseFloat((res as any).weight);
                    let h = parseFloat((res as any).height);
                    if (!w || !h) return "N/A";
                    if (h > 10) h = h / 100;
                    else h = h * 0.3048;
                    const bmiVal = w / (h * h);
                    return isFinite(bmiVal) ? bmiVal.toFixed(1) : "N/A";
                })(),
                room:
                    res.roomNumber && res.bedNumber
                        ? `${res.roomNumber}-${res.bedNumber}`
                        : res.roomNumber || "Not Assigned",
                sex: (res as any).gender || "N/A",
                status: (res as any).status || "ACTIVE",
                dob: res.dateOfBirth || "N/A",
                attendingPhysician:
                    (res as any).attendingPhysicianName ||
                    (res as any).attendingPhysician ||
                    "N/A",
                allergies: (res as any).allergies
                    ? String((res as any).allergies)
                        .split(",")
                        .map((a: string) => a.trim())
                        .filter(Boolean)
                    : [],
                admissionType: (res as any).admissionType || res.careType || "N/A",
            }
            : undefined;

    const fetchMedicationData = async () => {
        if (!selectedResident || !tenant?.id) return;
        setMarRefreshTrigger(prev => prev + 1);
        setLoading(true);
        try {
            let summaryStartStr = format(currentDate, "yyyy-MM-dd");
            let summaryEndStr = format(currentDate, "yyyy-MM-dd");
            if (activeTab === 1) {
                summaryStartStr = format(startOfWeek(currentDate), "yyyy-MM-dd");
                summaryEndStr = format(endOfWeek(currentDate), "yyyy-MM-dd");
            } else if (activeTab === 2) {
                summaryStartStr = format(startOfWeek(startOfMonth(currentDate)), "yyyy-MM-dd");
                summaryEndStr = format(endOfWeek(endOfMonth(currentDate)), "yyyy-MM-dd");
            }
            orderService.getDoseSummary(String(selectedResident.id), summaryStartStr, summaryEndStr)
                .then(summary => setDoseSummary(summary))
                .catch(err => console.error("Failed to fetch dose summary", err));

            const statNamesForResident = getStatNamesForCurrentResident();
            // Helper: strictly parse dates from various formats (dd/MM/yyyy or yyyy-MM-dd or ISO)
            const parseAnyDate = (raw: string | null | undefined): Date | null => {
                if (!raw || raw === "N/A") return null;
                const trimmed = raw.trim();
                if (trimmed === "") return null;

                // Try common formats strictly first using date-fns
                const formatsToTry = [
                    "dd/MM/yyyy",
                    "d/M/yyyy",
                    "yyyy-MM-dd",
                    "MM/dd/yyyy", // sometimes API sends MM/dd
                    "M/d/yyyy"
                ];

                for (const fmt of formatsToTry) {
                    const parsed = parse(trimmed, fmt, new Date());
                    if (!isNaN(parsed.getTime())) {
                        // Check if the parsed date makes logical sense (e.g. not year 0009)
                        if (parsed.getFullYear() > 2000) {
                            return parsed;
                        }
                    }
                }

                // Try ISO or native JS fallback
                const fallback = new Date(trimmed);
                if (!isNaN(fallback.getTime()) && fallback.getFullYear() > 2000) {
                    return fallback;
                }

                return null;
            };

            // Check if viewing date is within [med.startDate, med.endDate]
            const isWithinPrescriptionRange = (med: any, checkingDateStr: string) => {
                const checkingDate = startOfDay(parseISO(checkingDateStr + "T00:00:00"));
                const start = parseAnyDate(med.startDate || med.medicationStartDate);
                const end = parseAnyDate(med.endDate || med.refillDate || med.expiryDate);

                const isStat = isStatDoseSource(med) || statNamesForResident.has(normalizeMedName(med.medicationName));
                if (isStat) {
                    if (!start) return false;
                    return isSameDay(checkingDate, startOfDay(start));
                }

                if (start) {
                    const startDay = startOfDay(start);
                    if (checkingDate < startDay) return false;
                }
                if (end) {
                    const endDay = startOfDay(end);
                    if (checkingDate > endDay) return false;
                }
                return true;
            };

            const activeMedOrders: any[] = [];


            if (activeTab === 0) {
                const dateStr = format(currentDate, "yyyy-MM-dd");
                const dailyData = await orderService.getDailyMAR(String(selectedResident.id), dateStr);
                console.log("DAILY DATA RAW:", dailyData);

                const mappedMeds = (dailyData || [])
                    .filter((med: any) => isWithinPrescriptionRange(med, dateStr))
                    .map((med: any) => {
                        const order = activeMedOrders?.find(o =>
                            o.medicationItems?.some((m: any) =>
                                m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                                med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                            )
                        );
                        const medItem = order?.medicationItems?.find((m: any) =>
                            m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                            med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                        );
                        const stock = medItem?.updatedStock || "N/A";
                        const statFlag = isStatDoseSource(med) || statNamesForResident.has(normalizeMedName(med.medicationName));

                        return {
                            medicationName: med.medicationName,
                            dose: med.dosage,
                            route: medItem?.route || med.route || "Oral",
                            frequency: med.schedule,
                            isPrescriptionOnly: false,
                            updatedStock: stock,
                            isNarcotic: !!(() => {
                                const m = med as any;
                                const check = (obj: any) => Object.keys(obj).some(k =>
                                    (k.toLowerCase().includes('narcotic') || k.toLowerCase().includes('norcatic')) &&
                                    (obj[k] === true || obj[k] === "true" || obj[k] === 1 || obj[k] === "1")
                                );
                                return m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || check(m);
                            })(),
                            isCritical: !!(() => {
                                const m = med as any;
                                const check = (obj: any) => Object.keys(obj).some(k =>
                                    k.toLowerCase().includes('critical') &&
                                    (obj[k] === true || obj[k] === "true" || obj[k] === 1 || obj[k] === "1")
                                );
                                return m.isCritical || m.is_critical || check(m);
                            })(),
                            doses: (med.doses || []).map((d: any) => {
                                let parsedDate: Date | null = null;
                                if (d.dateTime) {
                                    parsedDate = parse(d.dateTime, "dd/MM/yyyy hh:mm a", new Date());
                                    if (isNaN(parsedDate.getTime())) {
                                        parsedDate = new Date(d.dateTime);
                                    }
                                }

                                const isStat = statFlag || isStatDoseSource(d) || isStatDoseSource(med);
                                const rawStatus = (d.status || "").toLowerCase();
                                let mappedStatus = d.status;
                                if (rawStatus === "pending") mappedStatus = isStat ? "Not Given" : "Upcoming";
                                else if (rawStatus === "nottaken" || rawStatus === "not_taken" || rawStatus === "missed" || rawStatus === "not given" || rawStatus === "not_given") mappedStatus = isStat ? "Not Given" : "Missed";
                                else if (rawStatus === "outofstock" || rawStatus === "out_of_stock") mappedStatus = "Out of Stock";
                                else if (rawStatus === "taken") mappedStatus = "Taken";
                                else if (rawStatus === "refused") mappedStatus = "Refused";
                                else if (rawStatus === "discontinued") mappedStatus = "Discontinued";
                                else if (rawStatus === "prn") mappedStatus = "PRN";
                                else if (rawStatus === "unable") mappedStatus = "Unable";

                                if (isStat && (mappedStatus === "Missed" || mappedStatus === "Not Taken" || mappedStatus === "Upcoming")) {
                                    mappedStatus = "Not Given";
                                }

                                return {
                                    ...d,
                                    status: mappedStatus,
                                    updatedStock: (d.updatedStock !== null && d.updatedStock !== undefined) ? d.updatedStock : stock,
                                    dateTime: parsedDate && !isNaN(parsedDate.getTime()) ? format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss") : d.dateTime,
                                    administered: (d.updatedByName && d.updatedByName !== "-") ? d.updatedByName : (d.createdByName !== "-" ? d.createdByName : ""),
                                    isStatDose: isStat,
                                };
                            })
                        };
                    });
                setMedications(mappedMeds);
            } else if (activeTab === 1) {
                const startStr = format(startOfWeek(currentDate), "yyyy-MM-dd");
                const endStr = format(endOfWeek(currentDate), "yyyy-MM-dd");
                const weeklyData = await orderService.getWeeklyMAR(String(selectedResident.id), startStr, endStr);

                const mappedMeds = (weeklyData || []).map((med: any) => {
                    const flattenedDoses: any[] = [];
                    const statFlag = isStatDoseSource(med) || statNamesForResident.has(normalizeMedName(med.medicationName));
                    (med.doses || []).forEach((doseSlot: any) => {
                        (doseSlot.records || []).forEach((record: any) => {
                            let parsedDate: Date | null = null;
                            if (record.dateTime) {
                                parsedDate = parse(record.dateTime, "dd/MM/yyyy hh:mm a", new Date());
                                if (isNaN(parsedDate.getTime())) parsedDate = new Date(record.dateTime);
                            }

                            const recordDateStr = parsedDate && !isNaN(parsedDate.getTime())
                                ? format(parsedDate, "yyyy-MM-dd")
                                : null;

                            // Skip this dose if it falls completely outside the medication's date range
                            if (recordDateStr && !isWithinPrescriptionRange(med, recordDateStr)) return;

                            const isStat = statFlag || isStatDoseSource(record) || isStatDoseSource(med);
                            const rawStatus = (record.status || "").toLowerCase();
                            let mappedStatus = record.status;
                            if (rawStatus === "pending") mappedStatus = isStat ? "Not Given" : "Upcoming";
                            else if (rawStatus === "nottaken" || rawStatus === "not_taken" || rawStatus === "missed" || rawStatus === "not given" || rawStatus === "not_given") mappedStatus = isStat ? "Not Given" : "Missed";
                            else if (rawStatus === "outofstock" || rawStatus === "out_of_stock") mappedStatus = "Out of Stock";
                            else if (rawStatus === "taken") mappedStatus = "Taken";
                            else if (rawStatus === "refused") mappedStatus = "Refused";
                            else if (rawStatus === "discontinued") mappedStatus = "Discontinued";
                            else if (rawStatus === "prn") mappedStatus = "PRN";
                            else if (rawStatus === "unable") mappedStatus = "Unable";

                            if (isStat && (mappedStatus === "Missed" || mappedStatus === "Not Taken" || mappedStatus === "Upcoming")) {
                                mappedStatus = "Not Given";
                            }

                            flattenedDoses.push({
                                ...record,
                                status: mappedStatus,
                                dateTime: parsedDate && !isNaN(parsedDate.getTime()) ? format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss") : record.dateTime,
                                administered: (record.updatedByName && record.updatedByName !== "-") ? record.updatedByName : (record.createdByName !== "-" ? record.createdByName : ""),
                                isStatDose: isStat,
                            });
                        });
                    });

                    const order = activeMedOrders?.find(o =>
                        o.medicationItems?.some((m: any) =>
                            m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                            med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                        )
                    );
                    const medItem = order?.medicationItems?.find((m: any) =>
                        m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                        med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                    );

                    return {
                        medicationName: med.medicationName,
                        dose: med.dose || med.dosage,
                        route: medItem?.route || med.route || "Oral",
                        frequency: med.schedule,
                        isPrescriptionOnly: false,
                        isNarcotic: !!(() => {
                            const m = med as any;
                            const check = (obj: any) => Object.keys(obj).some(k =>
                                (k.toLowerCase().includes('narcotic') || k.toLowerCase().includes('norcatic')) &&
                                (obj[k] === true || obj[k] === "true" || obj[k] === 1 || obj[k] === "1")
                            );
                            return m.isNarcotic || m.isNorcatiik || m.isNorcatick || m.is_narcotic || check(m);
                        })(),
                        isCritical: !!(() => {
                            const m = med as any;
                            const check = (obj: any) => Object.keys(obj).some(k =>
                                k.toLowerCase().includes('critical') &&
                                (obj[k] === true || obj[k] === "true" || obj[k] === 1 || obj[k] === "1")
                            );
                            return m.isCritical || m.is_critical || check(m);
                        })(),
                        doses: flattenedDoses
                    };
                }).filter((med: any) => med.doses.length > 0); // remove meds with no visible doses this week
                setMedications(mappedMeds);
            } else {
                let startStr: string = format(startOfWeek(startOfMonth(currentDate)), "yyyy-MM-dd");
                let endStr: string = format(endOfWeek(endOfMonth(currentDate)), "yyyy-MM-dd");
                const marData = await orderService.getMonthlyMAR(tenant.id!, String(selectedResident.id), startStr, endStr);

                const mappedMarData = (Array.isArray(marData) ? marData : []).map((med: any) => {
                    const flattenedDoses: any[] = [];
                    const statFlag = isStatDoseSource(med) || statNamesForResident.has(normalizeMedName(med.medicationName));
                    (med.doses || []).forEach((doseSlot: any) => {
                        (doseSlot.records || []).forEach((record: any) => {
                            let parsedDate: Date | null = null;
                            if (record.dateTime) {
                                parsedDate = parse(record.dateTime, "dd/MM/yyyy hh:mm a", new Date());
                                if (isNaN(parsedDate.getTime())) {
                                    parsedDate = new Date(record.dateTime);
                                }
                            }

                            const recordDateStr = parsedDate && !isNaN(parsedDate.getTime())
                                ? format(parsedDate, "yyyy-MM-dd")
                                : null;

                            // Skip this dose if it falls completely outside the medication's date range
                            if (recordDateStr && !isWithinPrescriptionRange(med, recordDateStr)) return;

                            const isStat = statFlag || isStatDoseSource(record) || isStatDoseSource(med);
                            const rawStatus = (record.status || "").toLowerCase();
                            let mappedStatus = record.status;
                            if (rawStatus === "pending") mappedStatus = isStat ? "Not Given" : "Upcoming";
                            else if (rawStatus === "nottaken" || rawStatus === "not_taken" || rawStatus === "missed" || rawStatus === "not given" || rawStatus === "not_given") mappedStatus = isStat ? "Not Given" : "Missed";
                            else if (rawStatus === "outofstock" || rawStatus === "out_of_stock") mappedStatus = "Out of Stock";
                            else if (rawStatus === "taken") mappedStatus = "Taken";
                            else if (rawStatus === "refused") mappedStatus = "Refused";
                            else if (rawStatus === "discontinued") mappedStatus = "Discontinued";
                            else if (rawStatus === "prn") mappedStatus = "PRN";
                            else if (rawStatus === "unable") mappedStatus = "Unable";

                            if (isStat && (mappedStatus === "Missed" || mappedStatus === "Not Taken" || mappedStatus === "Upcoming")) {
                                mappedStatus = "Not Given";
                            }

                            const dateOnlyMatch = (record.date || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                            let finalDate = record.date;
                            if (dateOnlyMatch) {
                                finalDate = `${dateOnlyMatch[3]}-${dateOnlyMatch[2]}-${dateOnlyMatch[1]}`;
                            } else if (recordDateStr) {
                                finalDate = recordDateStr;
                            }

                            flattenedDoses.push({
                                ...record,
                                status: mappedStatus,
                                dateTime: parsedDate && !isNaN(parsedDate.getTime()) ? format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss") : record.dateTime,
                                date: finalDate,
                                administered: (record.updatedByName && record.updatedByName !== "-") ? record.updatedByName : (record.createdByName !== "-" ? record.createdByName : ""),
                                isStatDose: isStat,
                            });
                        });
                    });

                    const order = activeMedOrders?.find(o =>
                        o.medicationItems?.some((m: any) =>
                            m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                            med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                        )
                    );
                    const medItem = order?.medicationItems?.find((m: any) =>
                        m.medicationName.toLowerCase().includes(med.medicationName.toLowerCase()) ||
                        med.medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
                    );

                    return {
                        ...med,
                        dose: med.dose || med.dosage,
                        route: medItem?.route || med.route || "Oral",
                        frequency: med.schedule || med.frequency,
                        isPrescriptionOnly: false,
                        doses: flattenedDoses
                    };
                });

                setMedications(mappedMarData);
            }
        } catch (err) {
            console.error("Failed to fetch medication data", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReport = async () => {
        if (!selectedResident) {
            setSnackbar({ open: true, message: "Please select a resident first", severity: "info" });
            return;
        }

        setIsReportLoading(true);
        try {
            const startDate = format(startOfMonth(currentDate), "yyyy-MM-dd");
            const endDate = format(endOfMonth(currentDate), "yyyy-MM-dd");
            const response = await apiClient.get<Blob>(
                `v1/medication-schedule/monthly/pdf`,
                {
                    params: {
                        residentId: selectedResident.id,
                        startDate,
                        endDate,
                    },
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const objectUrl = window.URL.createObjectURL(blob);

            let oldIframe = document.getElementById("med-print-iframe");
            if (oldIframe) {
                document.body.removeChild(oldIframe);
            }

            const iframe = document.createElement("iframe");
            iframe.id = "med-print-iframe";
            iframe.style.visibility = "hidden";
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "1px";
            iframe.style.height = "1px";
            iframe.style.border = "0";
            iframe.src = objectUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                setTimeout(() => {
                    try {
                        if (iframe.contentWindow) {
                            iframe.contentWindow.focus();
                            iframe.contentWindow.print();
                        }
                    } catch (printErr) {
                        console.error("Direct print failed:", printErr);
                    }
                }, 500);
            };
        } catch (err: any) {
            console.error("Failed to download medication schedule report", err);
            const message = err?.response?.data?.message || err?.message || "Unable to download report.";
            setSnackbar({ open: true, message, severity: "error" });
        } finally {
            setIsReportLoading(false);
        }
    };


    const parseScheduleToTimes = (schedule: string): string[] => {
        if (!schedule) return ["09:00"];
        const lower = schedule.toLowerCase();
        if (lower.includes("bid")) return ["09:00", "18:00"];
        if (lower.includes("tid")) return ["09:00", "13:00", "18:00"];
        if (lower.includes("qid")) return ["09:00", "13:00", "18:00", "22:00"];
        if (lower.includes("morning")) return ["08:00"];
        if (lower.includes("noon")) return ["12:00"];
        if (lower.includes("evening")) return ["17:00"];
        if (lower.includes("night") || lower.includes("hs")) return ["21:00"];
        return ["09:00"];
    };

    // Convert time slot to time (HH:mm format)
    const timeSlotToTime = (timeSlot: string): string => {
        switch (timeSlot) {
            case "Morning": return "08:00";
            case "Mid-AM": return "10:30";
            case "Noon": return "13:00";
            case "Afternoon": return "15:00";
            case "Evening": return "18:00";
            case "Night": return "21:00";
            default: return "09:00";
        }
    };

    // Get times for a medication considering timeSlot if schedule is "Once daily"
    const getMedicationTimes = (schedule: string, timeSlot?: string): string[] => {
        const lower = (schedule || "").toLowerCase();
        if (lower.includes("once daily") && timeSlot) {
            return [timeSlotToTime(timeSlot)];
        }
        return parseScheduleToTimes(schedule);
    };

    const handlePrevDate = () => {
        if (activeTab === 0) setCurrentDate(subDays(currentDate, 1));
        else if (activeTab === 1) setCurrentDate(subDays(currentDate, 7));
        else setCurrentDate(subMonths(currentDate, 1));
    };

    const handleNextDate = () => {
        if (activeTab === 0) setCurrentDate(addDays(currentDate, 1));
        else if (activeTab === 1) setCurrentDate(addDays(currentDate, 7));
        else setCurrentDate(addMonths(currentDate, 1));
    };

    const isNextDisabled = () => {
        return false;
    };

    const handleOpenUpdateDialog = (date: Date, med?: any) => {
        if (!selectedResident) {
            setSnackbar({ open: true, message: "Please select a resident first", severity: "info" });
            return;
        }
        if (med?.status === "Upcoming") {
            return;
        }
        const todayStart = startOfDay(new Date());
        if (isBefore(startOfDay(date), todayStart)) {
            const allowedRoles = ["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "OFFICER"];
            if (!user?.role || !allowedRoles.includes(user.role)) {
                // Remove snackbar as per request, tooltip will be shown on hover instead
                return;
            }
        }
        if (isAfter(startOfDay(date), startOfDay(new Date()))) {
            setSnackbar({ open: true, message: "Cannot update status for future dates", severity: "error" });
            return;
        }
        setSelectedDateForUpdate(date);
        setSelectedMed(med || null);

        // Always default to the current browser time when opening the dialog
        const defaultTime = format(new Date(), "HH:mm");

        setUpdateForm({
            medicationName: med?.medicationName || "",
            dosage: med?.dose || med?.doseAmount || "",
            status: med?.status || "",
            reason: med?.reason || "",
            results: (med?.result && !med.result.startsWith("Administered as ") && !med.result.startsWith("Scheduled Dose")) ? med.result : "",
            lowStock: med?.lowStock || false,
            time: defaultTime,
            updatedStock: med?.updatedStock ? String(med.updatedStock) : "",
        });


        setFormError(null);
        setIsUpdateDialogOpen(true);
    };

    const handleOpenDayDetails = (day: Date, doses: any[]) => {
        if (!selectedResident) return;
        setSelectedDayForDetails(day);
        setDosesForSelectedDay(doses);
        setIsDayDetailsDialogOpen(true);
    };

    const checkIsReasonMandatory = (
        day: Date | string | null | undefined,
        slotTimeStr?: string | null,
        dateTimeStr?: string | null
    ): boolean => {
        if (!day) return false;
        const targetDate = typeof day === "string" ? new Date(day) : new Date(day.getTime());
        if (isNaN(targetDate.getTime())) return false;

        const now = new Date();
        const todayStart = startOfDay(now);
        const cellDayStart = startOfDay(targetDate);

        // 1. Past date -> Reason mandatory for any authorized user
        if (isBefore(cellDayStart, todayStart)) {
            return true;
        }

        // 2. Today -> check if past active window
        if (cellDayStart.getTime() === todayStart.getTime()) {
            let effectiveSlotTime = slotTimeStr;
            if (!effectiveSlotTime && dateTimeStr) {
                try {
                    const dt = new Date(dateTimeStr);
                    if (!isNaN(dt.getTime())) {
                        effectiveSlotTime = format(dt, "HH:mm");
                    }
                } catch {
                    // ignore
                }
            }

            if (!effectiveSlotTime) return false;

            try {
                let hours = 0;
                let minutes = 0;

                const upper = effectiveSlotTime.trim().toUpperCase();
                const isPM = upper.includes("PM");
                const isAM = upper.includes("AM");
                const cleanTime = upper.replace(/(AM|PM)/g, "").trim();
                const parts = cleanTime.split(":").map(Number);

                if (parts.length >= 1 && !isNaN(parts[0])) {
                    hours = parts[0];
                    minutes = parts.length >= 2 && !isNaN(parts[1]) ? parts[1] : 0;
                    if (isPM && hours < 12) hours += 12;
                    if (isAM && hours === 12) hours = 0;
                }

                const slotDateTime = new Date();
                slotDateTime.setHours(hours, minutes, 0, 0);

                // If scheduled time has not arrived yet, it's on-time / upcoming
                if (now <= slotDateTime) {
                    return false;
                }

                // Scheduled time HAS passed. Check active window end.
                let windowEndHour = hours + 2; // fallback 2-hour buffer
                if (hours >= 6 && hours < 11) windowEndHour = 11;
                else if (hours >= 11 && hours < 15) windowEndHour = 15;
                else if (hours >= 15 && hours < 19) windowEndHour = 19;
                else if (hours >= 19 || hours < 6) windowEndHour = 24;

                const currentHourFloat = now.getHours() + (now.getMinutes() / 60);

                // If current time is past active window end -> Late -> Mandatory Reason
                if (currentHourFloat >= windowEndHour) {
                    return true;
                }
            } catch (e) {
                console.error("Error evaluating active window:", e);
            }
        }

        return false;
    };

    const handleUpdateStatus = async (statusOverride?: string, signatureFile?: File) => {
        if (!selectedResident || !selectedDateForUpdate) return;
        const finalStatus = statusOverride || updateForm.status;
        const statusToUse = statusOverride || updateForm.status;

        const isReasonReq = checkIsReasonMandatory(selectedDateForUpdate, selectedMed?.slotTime || updateForm.time, selectedMed?.dateTime);
        const enteredReason = (updateForm.results || updateForm.reason || "").trim();

        if ((isReasonReq || statusToUse === 'Unable' || statusToUse === 'NotTaken' || statusToUse === 'Missed') && !enteredReason) {
            setFormError("Reason is mandatory for late or past-date medication administration.");
            return;
        }

        setIsSubmitting(true);
        try {
            const statusToUse = statusOverride || updateForm.status;
            const backendStatus =
                statusToUse === "Missed" || statusToUse === "NotTaken" ? "NotTaken" :
                    statusToUse === "Out of Stock" || statusToUse === "OutOfStock" ? "OutOfStock" :
                        statusToUse;

            // Use the date from selectedDateForUpdate and the time from updateForm.time
            // to ensure accurate browser local time is recorded.
            const [hours, minutes] = (updateForm.time || format(new Date(), "HH:mm")).split(":").map(Number);
            const targetDateTime = new Date(selectedDateForUpdate);
            targetDateTime.setHours(hours, minutes, 0, 0);

            // Prioritize the original slotTime from the medication object
            let slotTimeString = "09:00";
            if (selectedMed?.slotTime) {
                // If the backend has it, use it (assumed format like "HH:mm" or "HH:mm:ss")
                slotTimeString = selectedMed.slotTime.split(':').slice(0, 2).join(':');
            } else if (selectedMed?.dateTime) {
                slotTimeString = format(new Date(selectedMed.dateTime), "HH:mm");
            } else {
                slotTimeString = format(targetDateTime, "HH:mm");
            }

            const payload: any = {
                residentId: String(selectedResident.id),
                medicationName: updateForm.medicationName,
                dosage: updateForm.dosage,
                dateTime: format(targetDateTime, "yyyy-MM-dd'T'HH:mm:ss"), // Actual administered time
                slotTime: slotTimeString, // The scheduled slot time like "07:00"
                medicationStatus: backendStatus,
                reason: updateForm.reason || "Scheduled Dose",
                schedule: selectedMed?.frequency || selectedMed?.schedule || updateForm.reason || "Scheduled Dose",
                result: updateForm.results,
                lowStock: updateForm.lowStock,
                updatedStock: updateForm.updatedStock ? Number(updateForm.updatedStock) : undefined,
                updatedByName: user?.name || user?.username || "-"
            };

            const noteIdToUpdate = selectedMed?.id || selectedMed?.apiId;
            let existingNoteId = noteIdToUpdate;

            if (!existingNoteId) {
                const nurseNotes = await clinicalNoteService.getNurseNotesByResident(String(selectedResident.id));
                const targetTime = selectedMed?.dateTime ? new Date(selectedMed.dateTime) : targetDateTime;
                const targetSlot = selectedMed?.slotTime || slotTimeString;

                const noteToUpdate = nurseNotes.find((n: any) => {
                    if (!n.dateTime) return false;
                    const d = new Date(n.dateTime);
                    const isSameFullDate = d.getFullYear() === targetTime.getFullYear() &&
                        d.getMonth() === targetTime.getMonth() &&
                        d.getDate() === targetTime.getDate();

                    if (n.medicationName !== updateForm.medicationName) return false;
                    if (!isSameFullDate) return false;

                    // Priority 1: Match exactly by record ID if we can (should be covered above but safe fallback)
                    if (n.id && n.id === noteIdToUpdate) return true;

                    // Priority 2: Match by slotTime (the best unique indicator for a medication on a given day)
                    if (n.slotTime && targetSlot) {
                        return n.slotTime.startsWith(targetSlot.substring(0, 5));
                    }

                    // Priority 3: fallback to approximate time if no slotTime
                    return d.getHours() === targetTime.getHours() &&
                        d.getMinutes() === targetTime.getMinutes();
                });
                existingNoteId = noteToUpdate?.id;
            }

            if (existingNoteId) {
                await clinicalNoteService.updateNurseNote(existingNoteId, payload, signatureFile);
            } else {
                await clinicalNoteService.createNurseNote(payload, signatureFile);
            }


            setSnackbar({ open: true, message: "Medication status updated successfully", severity: "success" });
            setIsUpdateDialogOpen(false);
            fetchMedicationData();
        } catch (err: any) {
            const errorMsg = err.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK"
                ? "Stock Not Available For This Medication.(Contact Facility Manager)"
                : (err.response?.data?.message || err.message || "Failed to update status");

            if (err.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK") {
                setFormError(errorMsg);
            } else {
                setSnackbar({ open: true, message: errorMsg, severity: "error" });
            }
            if (signatureFile) {
                throw err;
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleRuntimeMedicationSubmit = async () => {
        if (!selectedResident) {
            setFormError("Please select a resident");
            return;
        }
        if (
            !runtimeMedForm.medicationName ||
            !runtimeMedForm.dosage ||
            !runtimeMedForm.unit ||
            !runtimeMedForm.route ||
            !runtimeMedForm.startDate ||
            !runtimeMedForm.givenTime
        ) {
            setFormError("Medication name, dose, unit, route, date, and administration time are required.");
            return;
        }


        setIsSubmitting(true);
        try {
            const formatLocalDateTime = (date: any, useCurrentTime = false, timeOverride?: string) => {
                if (!date || isNaN(new Date(date).getTime())) return null;
                const pad = (num: number) => String(num).padStart(2, "0");
                const d = new Date(date);
                if (timeOverride) {
                    const [h, m] = timeOverride.split(':').map(Number);
                    d.setHours(h, m || 0, 0, 0);
                } else if (useCurrentTime) {
                    const now = new Date();
                    d.setHours(now.getHours(), now.getMinutes(), 0, 0);
                } else {
                    d.setHours(0, 0, 0, 0);
                }
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
            };

            const selectedTimeOfDay = runtimeMedForm.givenTime || undefined;

            let administrationTime = "";
            if (runtimeMedForm.givenTime) {
                const parts = runtimeMedForm.givenTime.split(':');
                if (parts.length === 2) {
                    administrationTime = `${runtimeMedForm.givenTime}:00`;
                } else {
                    administrationTime = runtimeMedForm.givenTime;
                }
            }

            // Payload assembly matching the requested curl structure
            const payload = {
                residentId: String(selectedResident.id),
                medicationName: runtimeMedForm.medicationName,
                dosage: `${runtimeMedForm.dosage}${runtimeMedForm.unit ? `${runtimeMedForm.unit}` : ""}`,
                route: runtimeMedForm.route,
                startDate: formatLocalDateTime(runtimeMedForm.startDate, false, selectedTimeOfDay),
                stockQuantity: Number(runtimeMedForm.stockQuantity) || 0,
                schedule: "STAT Dose",
                timeOfDay: selectedTimeOfDay,
                // Persist explicit source marker so UI can distinguish STAT button entries.
                source: "STAT_DOSE",
                isStatDose: true,
                isNarcotic: !!runtimeMedForm.isNarcotic,
                isCritical: !!runtimeMedForm.isCritical,
                narcoticStorageLocation: "",
                updatedByName: user?.name || user?.username || "-",
                administrationTime,
                reason: runtimeMedForm.note || ""
            };

            // Call the specialized API endpoint as requested
            await apiClient.post("/v1/EMR-medications/stat-dose", payload);
            registerStatMedicationName(runtimeMedForm.medicationName);

            setSnackbar({ open: true, message: "Medication prescribed successfully", severity: "success" });
            setIsRuntimeMedDialogOpen(false);
            setRuntimeMedForm({
                medicationName: "",
                dosage: "",
                unit: "",
                route: "" as any,
                startDate: new Date(),
                refillDate: "" as any,
                schedule: "STAT Dose",
                timeSlot: "",
                stockQuantity: "1",
                note: "",
                isNarcotic: false,
                isCritical: false,
                narcoticStorageLocation: "",
                givenTime: getLocalTimeStr(),
            });
            fetchMedicationData(); // Refresh list if needed
        } catch (error: any) {
            setFormError(error.message || "Failed to prescribe medication");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getAvailableStock = (medicationName: string) => {
        const order = activeOrders.find(o =>
            o.medicationItems?.some((m: any) =>
                m.medicationName.toLowerCase().includes(medicationName.toLowerCase()) ||
                medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
            )
        );
        if (!order) return "N/A";
        const item = order.medicationItems.find((m: any) =>
            m.medicationName.toLowerCase().includes(medicationName.toLowerCase()) ||
            medicationName.toLowerCase().includes(m.medicationName.toLowerCase())
        );
        return item?.updatedStock || "N/A";
    };

    //  MED PILL CHIP 
    const MedPill = ({ dose, onClick, hideStock = false, date }: { dose: any; onClick?: () => void; hideStock?: boolean; date?: Date }) => {
        const cfg = STATUS_CONFIG[dose.status] || STATUS_CONFIG["Upcoming"];

        // Logic for no-access tooltip
        const pillDate = date || (dose.dateTime ? new Date(dose.dateTime) : new Date());
        const isPastDate = isBefore(startOfDay(pillDate), startOfDay(new Date()));
        const isAdmin = ["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "OFFICER", "CAREGIVER", "NURSE"].includes(normalizedUserRole);
        const showNoAccessTooltip = isPastDate && !isAdmin;
        const canEdit = onClick && (isAdmin || !isPastDate) && dose.status !== "Upcoming";

        const pillContent = (
            <Box
                onClick={canEdit ? onClick : undefined}
                sx={{
                    borderRadius: "10px",
                    border: `1px solid ${cfg.color}30`,
                    borderLeft: `3px solid ${cfg.color}`,
                    bgcolor: cfg.bg,
                    cursor: canEdit ? "pointer" : (showNoAccessTooltip ? "help" : "default"),
                    transition: "all 0.15s ease",
                    "&:hover": canEdit ? { boxShadow: `0 2px 8px ${cfg.color}33`, transform: "translateY(-1px)" } : {},
                    overflow: "hidden",
                    p: 1,
                }}
            >
                {/* Medication Name */}
                <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                        color: COLORS.textPrimary,
                        display: "block",
                        fontSize: "0.75rem",
                        lineHeight: 1.3,
                        wordBreak: "break-word",
                        mb: 0.4,
                    }}
                >
                    {dose.medicationName}
                </Typography>

                {/* Time & Dose row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap", mb: 0.5 }}>
                    <TimeIcon sx={{ fontSize: 10, color: COLORS.textSecondary, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: "0.68rem", lineHeight: 1.2 }}>
                        {dose.dateTime ? format(new Date(dose.dateTime), "hh:mm a") : ""}
                        {dose.doseAmount ? ` · ${dose.doseAmount}` : ""}
                    </Typography>
                </Box>

                {/* Status Chip */}
                <StatusChip label={cfg.label} color={cfg.color} showStatIcon={!!dose.isStatDose} isNarcotic={!!dose.isNarcotic} isCritical={!!dose.isCritical} size="small" />

                {/* Stock (when not hidden) */}
                {!hideStock && (dose.status === "Missed" || dose.status === "Not Given" || dose.status === "Upcoming" || dose.status === "Taken" || dose.status === "Refused" || dose.status === "PRN" || dose.status === "Discontinued") && (
                    <Typography variant="caption" sx={{ color: COLORS.primary, fontSize: "0.65rem", fontWeight: 700, mt: 0.4, display: "block" }}>
                        Stock : {dose.updatedStock || "N/A"}
                    </Typography>
                )}

                {/* Administered by */}
                {dose.administered && (
                    <Typography variant="caption" sx={{ display: "block", color: COLORS.textSecondary, mt: 0.25, fontSize: "0.65rem" }}>
                        👤 {dose.administered}
                    </Typography>
                )}
            </Box>
        );

        const resultValue = String(dose.result || "").trim();
        const hasResult = resultValue.length > 0 &&
            !resultValue.startsWith("Administered as ") &&
            !resultValue.startsWith("Scheduled Dose");

        let finalTooltipTitle: React.ReactNode = "";

        if (showNoAccessTooltip) {
            finalTooltipTitle = (
                <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
                        You do not have access to edit previous medication. Please contact your Facility Manager.
                    </Typography>
                    {resultValue.length > 0 && (
                        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 1, mt: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)", display: 'block', mb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                                Result / Response
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', opacity: 0.9 }}>
                                {resultValue}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        } else if (hasResult) {
            finalTooltipTitle = (
                <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                        Result / Response
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.4 }}>
                        {resultValue}
                    </Typography>
                </Box>
            );
        }

        if (finalTooltipTitle) {
            return (
                <Tooltip title={finalTooltipTitle} arrow placement="bottom">
                    <span>{pillContent}</span>
                </Tooltip>
            );
        }

        return pillContent;
    };


    const renderTodayView = () => {
        const dateStr = format(currentDate, "yyyy-MM-dd");
        const canEditCurrentDate = isSameDay(currentDate, new Date()) || isBefore(startOfDay(currentDate), startOfDay(new Date()));
        const allDoses = medications.flatMap(med => {
            const doses = (med.doses || []).map((d: any) => ({ ...d, medicationName: med.medicationName, doseAmount: med.dose, route: med.route, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical }));
            if (med.isPrescriptionOnly) {
                const times = parseScheduleToTimes(med.frequency);
                // If we have a specific startDate with time, use that time for the dose slot on the start day
                if (med.startDate) {
                    const startDt = new Date(med.startDate);
                    const timeStr = format(startDt, "HH:mm");
                    if (timeStr !== "00:00") {
                        // If "Once daily" or only one time is returned, replace it. 
                        // For multi-dose schedules, we can either replace the first or just add it.
                        // Given the user's request for STAT doses, replacing/ensuring the current time slot is key.
                        const isStat = isStatDoseSource(med) || !!(med as any).isStatDose;
                        if (times.length <= 1 || med.frequency.toLowerCase().includes("once") || med.frequency.toLowerCase().includes("other") || isStat) {
                            return [{ status: isStat ? "Not Given" : (startDt < new Date() ? "Missed" : "Upcoming"), dateTime: `${dateStr}T${timeStr}:00`, medicationName: med.medicationName, doseAmount: med.dose, route: med.route, isStatDose: isStat, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical }];
                        }
                    }
                }
                const isStat = isStatDoseSource(med) || !!(med as any).isStatDose;
                return times.map(t => {
                    const dt = new Date(`${dateStr}T${t}:00`);
                    return { status: isStat ? "Not Given" : (dt < new Date() ? "Missed" : "Upcoming"), dateTime: `${dateStr}T${t}:00`, medicationName: med.medicationName, doseAmount: med.dose, route: med.route, isStatDose: isStat, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical };
                });
            }
            return doses.map((d: any) => {
                let st = d.status;
                const isStat = d.isStatDose ?? isStatDoseSource(med);
                if (st === "Upcoming" && d.dateTime && new Date(d.dateTime) < new Date()) st = isStat ? "Not Given" : "Missed";
                if (isStat && (st === "Missed" || st === "Not Taken" || st === "Upcoming")) st = "Not Given";
                return { ...d, status: st, isStatDose: isStat, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical };
            });
        }).filter((d: any) => (d.date || d.dateTime?.split("T")[0]) === dateStr);

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                {TIME_SLOTS.map((slot, i) => {
                    const dosesInSlot = allDoses.filter((d: any) => {
                        let h: number;
                        if (d.slotTime) {
                            // Normalize slotTime format (h:mm or h:mm AM/PM)
                            const timePart = d.slotTime.split(' ')[0];
                            const [hours, minutes] = timePart.split(':').map(Number);
                            h = hours;

                            // Adjust for PM if formatted with period
                            if (d.slotTime.toUpperCase().includes('PM') && h < 12) h += 12;
                            if (d.slotTime.toUpperCase().includes('AM') && h === 12) h = 0;
                        } else if (d.dateTime) {
                            h = new Date(d.dateTime).getHours();
                        } else {
                            return false;
                        }
                        return h >= slot.start && h < slot.end;
                    });
                    const isCurrent = isSameDay(currentDate, new Date()) &&
                        new Date().getHours() >= slot.start && new Date().getHours() < slot.end;

                    return (
                        <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
                            {/* Time Label */}
                            <Box sx={{
                                width: 90, flexShrink: 0, display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "flex-start", pt: 2, gap: 0.5,
                            }}>
                                <Typography fontSize="1.4rem">{slot.icon}</Typography>
                                <Typography variant="caption" fontWeight={700} sx={{ color: isCurrent ? COLORS.primary : COLORS.textSecondary, textAlign: "center", lineHeight: 1.2 }}>
                                    {slot.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: "0.65rem", textAlign: "center" }}>
                                    {slot.sub}
                                </Typography>
                            </Box>

                            {/* Slot Content */}
                            <Box sx={{
                                flex: 1, bgcolor: COLORS.surface,
                                border: isCurrent ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                                borderRadius: "14px",
                                p: 2, position: "relative",
                                boxShadow: isCurrent ? `0 0 0 4px ${COLORS.primaryLight}` : "0 1px 3px rgba(0,0,0,0.04)",
                                transition: "all 0.2s",
                            }}>
                                {isCurrent && (
                                    <Chip label="⚡ Active Window" size="small"
                                        sx={{ position: "absolute", top: -12, left: 16, bgcolor: COLORS.primary, color: "white", fontWeight: 700, fontSize: "0.7rem", height: 22 }}
                                    />
                                )}
                                {dosesInSlot.length === 0 ? (
                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontStyle: "italic" }}>No medications scheduled</Typography>
                                ) : (
                                    <Grid container spacing={1.5}>
                                        {dosesInSlot.map((dose: any, idx: number) => {
                                            const cfg = STATUS_CONFIG[dose.status] || STATUS_CONFIG["Upcoming"];
                                            const isPastDate = isBefore(startOfDay(currentDate), startOfDay(new Date()));
                                            const isAdmin = ["ADMIN", "FACILITY_ADMIN", "SUPER_ADMIN", "CLIENT_ADMIN", "OFFICER", "CAREGIVER", "NURSE"].includes(normalizedUserRole);
                                            const showNoAccessTooltip = isPastDate && !isAdmin;
                                            const canEdit = (canEditCurrentDate && dose.status !== "Taken" && dose.status !== "Upcoming") && (isAdmin || !isPastDate);

                                            const contentBox = (
                                                <Box
                                                    onClick={canEdit ? () => handleOpenUpdateDialog(currentDate, dose) : undefined}
                                                    sx={{
                                                        borderRadius: "12px", p: 1.5,
                                                        bgcolor: cfg.bg,
                                                        border: `1px solid ${cfg.color}30`,
                                                        borderLeft: `4px solid ${cfg.color}`,
                                                        cursor: canEdit ? "pointer" : (showNoAccessTooltip ? "help" : "default"),
                                                        transition: "all 0.2s",
                                                        "&:hover": canEdit ? { boxShadow: `0 4px 12px ${cfg.color}25`, transform: "translateY(-2px)" } : {},
                                                    }}
                                                >
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <StatusChip label={dose.status} color={cfg.color} showStatIcon={!!dose.isStatDose} isNarcotic={!!dose.isNarcotic} isCritical={!!dose.isCritical} size="small" />
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                                            <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: "0.68rem", fontWeight: 600 }}>
                                                                {dose.slotTime
                                                                    ? (dose.slotTime.includes(':')
                                                                        ? (() => {
                                                                            const [h, m] = dose.slotTime.split(':').map(Number);
                                                                            const period = h >= 12 ? 'PM' : 'AM';
                                                                            const hours12 = h % 12 || 12;
                                                                            return `${String(hours12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
                                                                        })()
                                                                        : dose.slotTime)
                                                                    : (dose.dateTime ? format(new Date(dose.dateTime), "hh:mm a") : "")
                                                                }
                                                            </Typography>
                                                            {dose.status !== "Upcoming" && dose.dateTime && dose.slotTime && (
                                                                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: "0.6rem", display: 'block', mt: 0.5 }}>
                                                                    {dose.status} Time: {format(new Date(dose.dateTime), "hh:mm a")}
                                                                </Typography>
                                                            )}
                                                            {(dose.status === "Missed" || dose.status === "Not Given" || dose.status === "Upcoming" || dose.status === "Taken" || dose.status === "Refused" || dose.status === "PRN" || dose.status === "Discontinued") && (
                                                                <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 700, fontSize: "0.7rem", mt: 0.5 }}>
                                                                    Stock : {dose.updatedStock || "N/A"}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight={700} noWrap sx={{ color: COLORS.textPrimary, fontSize: "0.85rem" }}>
                                                        {dose.medicationName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                                                        {dose.doseAmount}{dose.route ? ` · ${dose.route}` : ""}
                                                    </Typography>
                                                    {dose.administered && (
                                                        <Typography variant="caption" sx={{ display: "block", color: COLORS.textSecondary, mt: 0.5, fontSize: "0.65rem" }}>
                                                            👤 {dose.administered}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );

                                            const resultValue = String(dose.result || "").trim();
                                            const hasResult = resultValue.length > 0 &&
                                                !resultValue.startsWith("Administered as ") &&
                                                !resultValue.startsWith("Scheduled Dose");

                                            let finalTooltipTitle: React.ReactNode = "";

                                            if (showNoAccessTooltip) {
                                                finalTooltipTitle = (
                                                    <Box sx={{ p: 0.5 }}>
                                                        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
                                                            You do not have access to edit previous medication. Please contact your Facility Manager.
                                                        </Typography>
                                                        {resultValue.length > 0 && (
                                                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', pt: 1, mt: 0.5 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 800, color: "rgba(255,255,255,0.9)", display: 'block', mb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                                                                    Result / Response
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', opacity: 0.9 }}>
                                                                    {resultValue}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            } else if (hasResult) {
                                                finalTooltipTitle = (
                                                    <Box sx={{ p: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                                                            Result / Response
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.4 }}>
                                                            {resultValue}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }

                                            return (
                                                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                                    {finalTooltipTitle ? (
                                                        <Tooltip title={finalTooltipTitle} arrow placement="bottom">
                                                            <span>{contentBox}</span>
                                                        </Tooltip>
                                                    ) : contentBox}
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // ─── WEEKLY VIEW ───────────────────────────────────────────────────────────
    const renderWeeklyView = () => {
        const days = eachDayOfInterval({ start: startOfWeek(currentDate), end: endOfWeek(currentDate) });

        return (
            <Box sx={{ mt: 2, borderRadius: "16px", overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
                {/* Header Row */}
                <Box sx={{ display: "flex", bgcolor: COLORS.primary }}>
                    {days.map(day => {
                        const isToday = isSameDay(day, new Date());
                        return (
                            <Box key={day.toString()} sx={{
                                flex: 1, p: 1.5, textAlign: "center",
                                borderRight: days.indexOf(day) === 6 ? "none" : "1px solid rgba(255,255,255,0.15)",
                                bgcolor: isToday ? "rgba(255,255,255,0.18)" : "transparent",
                            }}>
                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600, display: "block", textTransform: "uppercase", fontSize: "0.65rem" }}>
                                    {format(day, "EEE")}
                                </Typography>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                                    mx: "auto", mt: 0.5,
                                    bgcolor: isToday ? "white" : "transparent",
                                }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: isToday ? COLORS.primary : "white", fontSize: "0.9rem" }}>
                                        {format(day, "d")}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Body Row */}
                <Box sx={{ display: "flex", bgcolor: COLORS.surface }}>
                    {days.map(day => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isToday = isSameDay(day, new Date());
                        const isFuture = isAfter(startOfDay(day), startOfDay(new Date()));

                        const dosesForDay = medications.flatMap(med => {
                            const doses = (med.doses || [])
                                .filter((d: any) => (d.date || d.dateTime?.split("T")[0]) === dateStr)
                                .map((d: any) => ({ ...d, medicationName: med.medicationName, doseAmount: med.dose, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical }));
                            if (med.isPrescriptionOnly && !isFuture) {
                                const times = parseScheduleToTimes(med.frequency);
                                if (med.startDate) {
                                    const startDt = new Date(med.startDate);
                                    const timeStr = format(startDt, "HH:mm");
                                    if (timeStr !== "00:00") {
                                        if (times.length <= 1 || med.frequency.toLowerCase().includes("once") || med.frequency.toLowerCase().includes("other")) {
                                            return [{ status: startDt < new Date() ? "Missed" : "Upcoming", dateTime: `${dateStr}T${timeStr}:00`, medicationName: med.medicationName, doseAmount: med.dose, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical }];
                                        }
                                    }
                                }
                                return times.map(t => {
                                    const dt = new Date(`${dateStr}T${t}:00`);
                                    return { status: dt < new Date() ? "Missed" : "Upcoming", dateTime: `${dateStr}T${t}:00`, medicationName: med.medicationName, doseAmount: med.dose, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical };
                                });
                            }
                            return doses.map((d: any) => {
                                let st = d.status;
                                if (st === "Upcoming" && d.dateTime && new Date(d.dateTime) < new Date()) st = "Missed";
                                return { ...d, status: st, isNarcotic: !!med.isNarcotic, isCritical: !!med.isCritical };
                            });
                        }).sort((a, b) => (a.dateTime || "").localeCompare(b.dateTime || ""));

                        const takenCount = dosesForDay.filter((d: any) => d.status === "Taken").length;
                        const missedCount = dosesForDay.filter((d: any) => d.status === "Missed").length;

                        const isPast = isBefore(startOfDay(day), startOfDay(new Date()));
                        const isScheduled = dosesForDay.length > 0;
                        const isDisabled = isFuture || isPast;

                        return (
                            <Box key={day.toString()}
                                onClick={() => dosesForDay.length > 0 && handleOpenDayDetails(day, dosesForDay)}
                                sx={{
                                    flex: 1, minHeight: 480,
                                    borderRight: days.indexOf(day) === 6 ? "none" : `1px solid ${COLORS.border}`,
                                    bgcolor: isToday ? "#EFF8FF" : isDisabled ? "#FAFBFC" : COLORS.surface,
                                    opacity: isDisabled ? 0.55 : 1,
                                    cursor: dosesForDay.length > 0 ? "pointer" : "default",
                                    transition: "background-color 0.15s",
                                    "&:hover": dosesForDay.length > 0 ? { bgcolor: "#F0F9FF" } : {},
                                }}>
                                {/* Day Summary Bar */}
                                {dosesForDay.length > 0 && (
                                    <Box sx={{ p: 1, borderBottom: `1px solid ${COLORS.border}`, display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                                        {takenCount > 0 && <Chip label={`✓ ${takenCount}`} size="small" sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#F0FDF4", color: "#16A34A", fontWeight: 700 }} />}
                                        {missedCount > 0 && <Chip label={`✗ ${missedCount}`} size="small" sx={{ height: 18, fontSize: "0.62rem", bgcolor: "#FEF2F2", color: "#DC2626", fontWeight: 700 }} />}
                                    </Box>
                                )}

                                {/* Dose List */}
                                <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                                    {dosesForDay.length === 0 ? (
                                        <Typography variant="caption" sx={{ color: COLORS.textSecondary, display: "block", textAlign: "center", mt: 6, fontStyle: "italic" }}>— none —</Typography>
                                    ) : dosesForDay.map((dose: any, idx: number) => (
                                        <MedPill key={idx} dose={dose} date={day} hideStock={true} onClick={() => handleOpenDayDetails(day, dosesForDay)} />
                                    ))}
                                </Box>

                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // ─── MONTHLY VIEW ──────────────────────────────────────────────────────────
    const renderMonthlyView = () => {
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const days = eachDayOfInterval({ start: startOfWeek(monthStart), end: endOfWeek(monthEnd) });
        const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        return (
            <Box sx={{ mt: 2, borderRadius: "16px", overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
                {/* Week header */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", bgcolor: COLORS.primary }}>
                    {DAY_NAMES.map(d => (
                        <Box key={d} sx={{ p: 1.5, textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem" }}>{d}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar grid */}
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", bgcolor: COLORS.background }}>
                    {days.map(day => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isToday = isSameDay(day, new Date());
                        const isFuture = isAfter(startOfDay(day), startOfDay(new Date()));
                        const isCurrentMonth = isSameMonth(day, currentDate);

                        const dosesForDay = medications.flatMap(med =>
                            (med.doses || [])
                                .filter((d: any) => (d.date || d.dateTime?.split("T")[0]) === dateStr)
                                .map((d: any) => {
                                    let st = d.status;
                                    if (st === "Upcoming" && d.dateTime && new Date(d.dateTime) < new Date()) st = "Missed";
                                    return { ...d, medicationName: med.medicationName, doseAmount: med.dose, status: st, isNarcotic: !!med.isNarcotic };
                                })
                        ).sort((a, b) => (a.dateTime || "").localeCompare(b.dateTime || ""));

                        const taken = dosesForDay.filter((d: any) => d.status === "Taken").length;
                        const missed = dosesForDay.filter((d: any) => d.status === "Missed").length;
                        const total = dosesForDay.length;

                        const isPast = isBefore(startOfDay(day), startOfDay(new Date()));
                        const isScheduled = dosesForDay.length > 0;
                        const isDisabled = !isScheduled;

                        return (
                            <Box
                                key={day.toString()}
                                onClick={() => !isDisabled && handleOpenDayDetails(day, dosesForDay)}
                                sx={{
                                    minHeight: 130, p: 1,
                                    borderRight: "1px solid " + COLORS.border,
                                    borderBottom: "1px solid " + COLORS.border,
                                    bgcolor: isToday ? "#EFF8FF" : (!isCurrentMonth || isDisabled) ? "#F8FAFC" : COLORS.surface,
                                    opacity: isDisabled ? 0.45 : 1,
                                    cursor: !isDisabled ? "pointer" : "default",
                                    transition: "background-color 0.15s",
                                    "&:hover": (!isDisabled) ? { bgcolor: "#F0F9FF" } : {},
                                }}
                            >
                                {/* Date number */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                                    <Box sx={{
                                        width: 26, height: 26, borderRadius: "50%",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        bgcolor: isToday ? COLORS.primary : "transparent",
                                    }}>
                                        <Typography variant="caption" fontWeight={700} sx={{
                                            color: isToday ? "white" : !isCurrentMonth ? COLORS.textSecondary : COLORS.textPrimary,
                                            fontSize: "0.8rem",
                                        }}>
                                            {format(day, "d")}
                                        </Typography>
                                    </Box>
                                    {total > 0 && (
                                        <Box sx={{ display: "flex", gap: 0.4 }}>
                                            {taken > 0 && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#16A34A" }} />}
                                            {missed > 0 && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#DC2626" }} />}
                                        </Box>
                                    )}
                                </Box>

                                {/* Med events */}
                                <Stack spacing={0.4}>
                                    {dosesForDay.slice(0, 4).map((dose: any, idx: number) => {
                                        const cfg = STATUS_CONFIG[dose.status] || STATUS_CONFIG["Upcoming"];
                                        return (
                                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5, py: 0.25, borderRadius: "4px", bgcolor: cfg.bg }}>
                                                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: cfg.color, flexShrink: 0 }} />
                                                <Typography noWrap variant="caption" fontWeight={600} sx={{ fontSize: "0.62rem", color: COLORS.textPrimary, flex: 1 }}>
                                                    {dose.medicationName}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                    {dosesForDay.length > 4 && (
                                        <Typography variant="caption" sx={{ fontSize: "0.6rem", color: COLORS.primary, fontWeight: 700, pl: 0.5 }}>
                                            +{dosesForDay.length - 4} more
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // ─── STATS ROW ─────────────────────────────────────────────────────────────
    const getAllCurrentDoses = () => {
        const days = activeTab === 0
            ? [currentDate]
            : (activeTab === 1
                ? eachDayOfInterval({ start: startOfWeek(currentDate), end: endOfWeek(currentDate) })
                : eachDayOfInterval({ start: startOfWeek(startOfMonth(currentDate)), end: endOfWeek(endOfMonth(currentDate)) }));

        return days.flatMap(day => {
            const dateStr = format(day, "yyyy-MM-dd");
            const isFutureDay = isAfter(startOfDay(day), startOfDay(new Date()));
            return medications.flatMap(med => {
                const doses = (med.doses || [])
                    .filter((d: any) => (d.date || d.dateTime?.split("T")[0]) === dateStr)
                    .map((d: any) => ({ ...d, medicationName: med.medicationName, doseAmount: med.dose, frequency: med.frequency, route: med.route }));
                if (activeTab !== 2 && med.isPrescriptionOnly && !isFutureDay) {
                    const times = parseScheduleToTimes(med.frequency);
                    if (med.startDate) {
                        const startDt = new Date(med.startDate);
                        const timeStr = format(startDt, "HH:mm");
                        if (timeStr !== "00:00") {
                            if (times.length <= 1 || med.frequency.toLowerCase().includes("once") || med.frequency.toLowerCase().includes("other")) {
                                return [{ status: startDt < new Date() ? "Missed" : "Upcoming", dateTime: `${dateStr}T${timeStr}:00`, medicationName: med.medicationName, doseAmount: med.dose, frequency: med.frequency, route: med.route }];
                            }
                        }
                    }
                    return times.map(t => {
                        const dt = new Date(`${dateStr}T${t}:00`);
                        return { status: dt < new Date() ? "Missed" : "Upcoming", dateTime: `${dateStr}T${t}:00`, medicationName: med.medicationName, doseAmount: med.dose, frequency: med.frequency, route: med.route };
                    });
                }
                return doses.map((d: any) => {
                    let st = d.status;
                    if (st === "Upcoming" && d.dateTime && new Date(d.dateTime) < new Date()) st = "Missed";
                    return { ...d, status: st };
                });
            });
        });
    };

    const handleMissedBoxClick = () => {
        if (!selectedResident) return;
        const todayStart = startOfDay(new Date());
        const missed = getAllCurrentDoses().filter(
            (d: any) =>
                d.status === "Missed" &&
                d.dateTime &&
                !isNaN(new Date(d.dateTime).getTime())
        ).sort((a: any, b: any) => new Date(b.dateTime || 0).getTime() - new Date(a.dateTime || 0).getTime());
        setMissedDosesList(missed);
        setSelectedMissedDoses([]);
        setIsMissedDialogOpen(true);
    };

    const handleRefusedBoxClick = () => {
        if (!selectedResident) return;
        const refused = getAllCurrentDoses().filter(
            (d: any) => d.status === "Refused"
        ).sort((a, b) => new Date(b.dateTime || 0).getTime() - new Date(a.dateTime || 0).getTime());
        setRefusedDosesList(refused);
        setIsRefusedDialogOpen(true);
    };

    const handleToggleMissedDose = (index: number) => {
        setSelectedMissedDoses(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedMissedDoses(missedDosesList.map((_, idx) => idx));
        } else {
            setSelectedMissedDoses([]);
        }
    };

    const handleBatchUpdateStatus = async (status: string, signatureBase64?: string) => {
        if (!selectedResident || selectedMissedDoses.length === 0) return;

        if (status === 'Taken' && !signatureBase64) {
            const hasNarcoticSelected = selectedMissedDoses.some(idx => {
                const dose = missedDosesList[idx];
                return !!(
                    dose?.isNarcotic ||
                    dose?.isNorcatiik ||
                    dose?.isNorcatick ||
                    dose?.is_narcotic ||
                    medications.find((m: any) => normalizeMedName(m.medicationName) === normalizeMedName(dose?.medicationName))?.isNarcotic
                );
            });

            if (hasNarcoticSelected) {
                setBulkUpdatePendingStatus(status);
                setBulkUpdateSource('Missed');
                setSelectedWitnessId('');
                setWitnessSignatureData(null);
                setNarcoticDialogOpen(true);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const bulkNotes = selectedMissedDoses.map(idx => {
                const dose = missedDosesList[idx];
                const backendStatus =
                    status === "Missed" || status === "NotTaken" ? "NotTaken" :
                        status === "Out of Stock" || status === "OutOfStock" ? "OutOfStock" :
                            status;

                let slotTimeString = "09:00";
                if (dose?.slotTime) {
                    slotTimeString = dose.slotTime.split(':').slice(0, 2).join(':');
                } else if (dose?.dateTime) {
                    slotTimeString = format(new Date(dose.dateTime), "HH:mm");
                }

                return {
                    id: dose.id || dose.apiId,
                    residentId: String(selectedResident.id),
                    medicationName: dose.medicationName || "Unknown",
                    dosage: dose.doseAmount || dose.dose || "N/A",
                    reason: "Batch update from missed medications list",
                    result: "",
                    schedule: dose.schedule || dose.frequency || "N/A",
                    medicationStatus: backendStatus,
                    slotTime: slotTimeString,
                    dateTime: dose.dateTime || new Date().toISOString(),
                    updatedByName: user?.name || user?.username || "-",
                    signature: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureBase64: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureData: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureString: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    adminSignature: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    adminSignatureBase64: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    witnessSignature: witnessSignatureData ? witnessSignatureData.split(',')[1] : null,
                    witnessId: selectedWitnessId || null
                };
            });

            const response = await clinicalNoteService.createBulkNurseNotes(String(selectedResident.id), bulkNotes);

            const succeeded: any[] = response?.succeeded || [];
            const failed: any[] = response?.failed || [];

            // Immediately patch local medications state with correct stock from succeeded items
            if (succeeded.length > 0) {
                const stockMap = new Map<string, number>();
                succeeded.forEach((s: any) => {
                    if (s.medicationName && s.updatedStock != null) {
                        const key = `${(s.medicationName || "").toLowerCase()}::${s.slotTime}`;
                        stockMap.set(key, s.updatedStock);
                    }
                });
                setMedications((prev: any[]) => prev.map((med: any) => ({
                    ...med,
                    doses: (med.doses || []).map((dose: any) => {
                        const key = `${(med.medicationName || "").toLowerCase()}::${dose.slotTime}`;
                        if (stockMap.has(key)) {
                            return { ...dose, updatedStock: stockMap.get(key) };
                        }
                        return dose;
                    })
                })));
            }

            if (failed.length === 0) {

                // All succeeded
                setSnackbar({ open: true, message: `${succeeded.length} medication(s) successfully updated to ${status}.`, severity: "success" });
            } else if (succeeded.length > 0) {
                // Partial success — build a readable list of what failed
                const failedNames = failed
                    .map((f: any) => {
                        if (f.errorCode === "MEDICATION_OUT_OF_STOCK") return `${f.medicationName} (Out of Stock)`;
                        return `${f.medicationName} (${f.errorMessage || f.errorCode || "Error"})`;
                    })
                    .join(", ");
                setSnackbar({
                    open: true,
                    message: `${succeeded.length} updated ✓ | ${failed.length} failed: ${failedNames}`,
                    severity: "warning"
                });
            } else {
                // All failed
                const failedNames = failed
                    .map((f: any) => {
                        if (f.errorCode === "MEDICATION_OUT_OF_STOCK") return `${f.medicationName} (Out of Stock)`;
                        return `${f.medicationName} (${f.errorMessage || f.errorCode || "Error"})`;
                    })
                    .join(", ");
                setSnackbar({ open: true, message: `All updates failed: ${failedNames}`, severity: "error" });
            }

            setIsMissedDialogOpen(false);
            fetchMedicationData();
        } catch (err: any) {
            const failedArr: any[] = err.response?.data?.failed || [];
            if (failedArr.length > 0) {
                const failedNames = failedArr
                    .map((f: any) => f.errorCode === "MEDICATION_OUT_OF_STOCK" ? `${f.medicationName} (Out of Stock)` : `${f.medicationName}`)
                    .join(", ");
                setSnackbar({ open: true, message: `Failed: ${failedNames}`, severity: "error" });
            } else {
                const errorMsg = err.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK"
                    ? "Stock Not Available For This Medication. (Contact Facility Manager)"
                    : (err.response?.data?.message || err.message || "Failed to update medications");
                setSnackbar({ open: true, message: errorMsg, severity: "error" });
            }
            fetchMedicationData();
        } finally {
            setIsSubmitting(false);
            setSelectedMissedDoses([]);
        }
    };

    const handleBatchUpdateRefused = async (status: string, signatureBase64?: string) => {
        if (!selectedResident || selectedRefusedDoses.length === 0) return;

        if (status === 'Taken' && !signatureBase64) {
            const hasNarcoticSelected = selectedRefusedDoses.some(idx => {
                const dose = refusedDosesList[idx];
                return !!(
                    dose?.isNarcotic ||
                    dose?.isNorcatiik ||
                    dose?.isNorcatick ||
                    dose?.is_narcotic ||
                    medications.find((m: any) => normalizeMedName(m.medicationName) === normalizeMedName(dose?.medicationName))?.isNarcotic
                );
            });

            if (hasNarcoticSelected) {
                setBulkUpdatePendingStatus(status);
                setBulkUpdateSource('Refused');
                setSelectedWitnessId('');
                setWitnessSignatureData(null);
                setNarcoticDialogOpen(true);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const bulkNotes = selectedRefusedDoses.map(idx => {
                const dose = refusedDosesList[idx];
                const backendStatus =
                    status === "Missed" || status === "NotTaken" ? "NotTaken" :
                        status === "Out of Stock" || status === "OutOfStock" ? "OutOfStock" :
                            status;

                let slotTimeString = "09:00";
                if (dose?.slotTime) {
                    slotTimeString = dose.slotTime.split(':').slice(0, 2).join(':');
                } else if (dose?.dateTime) {
                    slotTimeString = format(new Date(dose.dateTime), "HH:mm");
                }

                return {
                    id: dose.id || dose.apiId,
                    residentId: String(selectedResident.id),
                    medicationName: dose.medicationName || "Unknown",
                    dosage: dose.doseAmount || dose.dose || "N/A",
                    reason: "Batch update from refused medications list",
                    result: "",
                    schedule: dose.schedule || dose.frequency || "N/A",
                    medicationStatus: backendStatus,
                    slotTime: slotTimeString,
                    dateTime: dose.dateTime || new Date().toISOString(),
                    updatedByName: user?.name || user?.username || "-",
                    signature: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureBase64: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureData: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    signatureString: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    adminSignature: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    adminSignatureBase64: signatureBase64 ? signatureBase64.split(',')[1] : null,
                    witnessSignature: witnessSignatureData ? witnessSignatureData.split(',')[1] : null,
                    witnessId: selectedWitnessId || null
                };
            });
            const response = await clinicalNoteService.createBulkNurseNotes(String(selectedResident.id), bulkNotes);

            const succeeded: any[] = response?.succeeded || [];
            const failed: any[] = response?.failed || [];

            if (failed.length === 0) {
                setSnackbar({ open: true, message: `${succeeded.length} medication(s) successfully updated to ${status}.`, severity: "success" });
            } else if (succeeded.length > 0) {
                const failedNames = failed
                    .map((f: any) => {
                        if (f.errorCode === "MEDICATION_OUT_OF_STOCK") return `${f.medicationName} (Out of Stock)`;
                        return `${f.medicationName} (${f.errorMessage || f.errorCode || "Error"})`;
                    })
                    .join(", ");
                setSnackbar({
                    open: true,
                    message: `${succeeded.length} updated ✓ | ${failed.length} failed: ${failedNames}`,
                    severity: "warning"
                });
            } else {
                const failedNames = failed
                    .map((f: any) => {
                        if (f.errorCode === "MEDICATION_OUT_OF_STOCK") return `${f.medicationName} (Out of Stock)`;
                        return `${f.medicationName} (${f.errorMessage || f.errorCode || "Error"})`;
                    })
                    .join(", ");
                setSnackbar({ open: true, message: `All updates failed: ${failedNames}`, severity: "error" });
            }

            setIsRefusedDialogOpen(false);
            fetchMedicationData();
        } catch (err: any) {
            const failedArr: any[] = err.response?.data?.failed || [];
            if (failedArr.length > 0) {
                const failedNames = failedArr
                    .map((f: any) => f.errorCode === "MEDICATION_OUT_OF_STOCK" ? `${f.medicationName} (Out of Stock)` : `${f.medicationName}`)
                    .join(", ");
                setSnackbar({ open: true, message: `Failed: ${failedNames}`, severity: "error" });
            } else {
                const errorMsg = err.response?.data?.errorCode === "MEDICATION_OUT_OF_STOCK"
                    ? "Stock Not Available For This Medication. (Contact Facility Manager)"
                    : (err.response?.data?.message || err.message || "Failed to update medications");
                setSnackbar({ open: true, message: errorMsg, severity: "error" });
            }
            fetchMedicationData();
        } finally {
            setIsSubmitting(false);
            setSelectedRefusedDoses([]);
        }
    };

    const handleEditMissedDose = (dose: any) => {
        setIsMissedDialogOpen(false);
        handleOpenUpdateDialog(dose.dateTime ? new Date(dose.dateTime) : new Date(), dose);
    };

    const renderEmptyState = () => (
        <Box sx={{ py: 12, textAlign: "center" }}>
            <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "rgba(72, 202, 228, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
                <PharmacyIcon sx={{ color: "#48CAE4", fontSize: 40 }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
                No Resident Selected
            </Typography>
            <Typography>
                Please search and select a resident from the menu above to view and manage their medication schedule.
            </Typography>
        </Box>
    );

    const renderStatsRow = () => {
        const allDoses = getAllCurrentDoses();
        const getApiCount = (keys: string[]): number | undefined => {
            if (!doseSummary || typeof doseSummary !== "object") return undefined;
            for (const k of keys) {
                if ((doseSummary as any)[k] !== undefined && (doseSummary as any)[k] !== null) {
                    const val = Number((doseSummary as any)[k]);
                    if (!isNaN(val)) return val;
                }
                const lowerKey = k.toLowerCase();
                const foundKey = Object.keys(doseSummary).find(key => key.toLowerCase() === lowerKey);
                if (foundKey && (doseSummary as any)[foundKey] !== undefined && (doseSummary as any)[foundKey] !== null) {
                    const val = Number((doseSummary as any)[foundKey]);
                    if (!isNaN(val)) return val;
                }
            }
            return undefined;
        };

        const totalValue = allDoses.length;
        const takenValue = allDoses.filter((d: any) => d.status === "Taken").length;
        const missedValue = allDoses.filter((d: any) => d.status === "Missed").length;
        const refusedValue = allDoses.filter((d: any) => d.status === "Refused").length;
        const upcomingValue = allDoses.filter((d: any) => d.status === "Upcoming" || d.status === "PENDING").length;
        const discontinuedValue = allDoses.filter((d: any) => d.status === "Discontinued").length;

        const stats = [
            { label: "Total Doses", value: totalValue, color: COLORS.primary, bg: COLORS.primaryLight },
            { label: "Taken", value: takenValue, color: "#16A34A", bg: "#F0FDF4" },
            { label: "Missed", value: missedValue, color: "#DC2626", bg: "#FEF2F2" },
            { label: "Refused", value: refusedValue, color: "#f06292", bg: "#fce4ec" },
            { label: "Upcoming", value: upcomingValue, color: "#7C3AED", bg: "#F5F3FF" },
            { label: "Discontinued", value: discontinuedValue, color: "#D97706", bg: "#FFFBEB" },
        ];
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                    gap: 2,
                    mb: 3
                }}
            >
                {stats.map(s => {
                    const isInteractive = activeTab === 0 && (s.label === "Missed" || s.label === "Refused");
                    return (
                        <Box
                            key={s.label}
                            onClick={isInteractive && s.label === "Missed" ? handleMissedBoxClick : isInteractive && s.label === "Refused" ? handleRefusedBoxClick : undefined}
                            sx={{
                                bgcolor: s.bg,
                                borderRadius: "14px",
                                p: 2,
                                border: `1px solid ${s.color}20`,
                                textAlign: "center",
                                cursor: isInteractive ? "pointer" : "default",
                                transition: "all 0.2s",
                                "&:hover": isInteractive ? { boxShadow: `0 4px 12px ${s.color}20`, transform: "translateY(-2px)" } : {}
                            }}
                        >
                            <Typography variant="h4" fontWeight={800} sx={{ color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                            <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>{s.label}</Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // ─── MAIN RENDER ───────────────────────────────────────────────────────────
    return (
        <Box sx={{ bgcolor: COLORS.background, minHeight: "100vh", py: { xs: 2, md: 3 }, px: 0 }}>
            <style>{`
                @media print {
                    @page { size: A4 landscape; margin: 1cm; }
                    body { background-color: white !important; }
                    .no-print { display: none !important; }
                }
            `}</style>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>

            {/* ── Page Header ── */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, ml: 0 }}>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center' }}>
                        <PharmacyIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight="700"
                            sx={{ mb: 0.5, color: "#1A2B45" }}
                        >
                            Medication Schedule
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 500 }}>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: "0.85rem" }}>
                                {selectedResident ? ((selectedResident as any).residentName || `${selectedResident.firstName} ${selectedResident.lastName}`) : "Select a resident to get started"}
                            </Typography>
                            {selectedResident && (
                                <>
                                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: COLORS.border }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.primary }}>
                                        <TimeIcon sx={{ fontSize: 14 }} />
                                        <Typography variant="caption" fontWeight={700}>
                                            {format(now, "hh:mm:ss a")}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
                {selectedResident && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            width: { xs: "auto", sm: "100%", lg: "auto" },
                            "& > button": {
                                flex: { xs: "none", sm: "1 1 auto", lg: "none" },
                                whiteSpace: { xs: "normal", sm: "nowrap", lg: "normal" }
                            }
                        }}
                        className="no-print"
                    >
                        <Button
                            variant="contained"
                            startIcon={<HistoryIcon />}
                            onClick={() => {
                                if (!selectedResident) {
                                    setSnackbar({ open: true, message: "Please select a resident first", severity: "info" });
                                    return;
                                }
                                setIsMarDialogOpen(true);
                            }}
                            sx={{
                                borderRadius: "10px",
                                bgcolor: COLORS.surface,
                                color: COLORS.primary,
                                border: `1px solid ${COLORS.primary}`,
                                textTransform: "none",
                                fontWeight: 600,
                                "&:hover": { bgcolor: COLORS.primaryLight, border: `1px solid ${COLORS.primary}` }
                            }}
                        >
                            MEDICATION ADMINISTRATION RECORD
                        </Button>
                        <Button variant="contained" startIcon={<MedicationIcon />} onClick={() => {
                            if (!selectedResident) {
                                setSnackbar({ open: true, message: "Please select a resident first", severity: "info" });
                                return;
                            }
                            // Reset form: only Start Date defaults to now, default schedule to STAT Dose
                            setRuntimeMedForm({
                                medicationName: "",
                                dosage: "",
                                unit: "",
                                route: "" as any,
                                startDate: new Date(),
                                refillDate: "" as any,
                                schedule: "STAT Dose",
                                timeSlot: "",
                                stockQuantity: "1",
                                note: "",
                                isNarcotic: false,
                                narcoticStorageLocation: "",
                                givenTime: getLocalTimeStr(),
                            });
                            setFormError(null);
                            fetchSchedules();
                            fetchRoutes();
                            setIsRuntimeMedDialogOpen(true);
                        }}
                            sx={{ borderRadius: "10px", bgcolor: "#48CAE4", color: "#000000", textTransform: "none", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}>
                            STAT Dose
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            onClick={handlePrintReport}
                            disabled={isReportLoading}
                            sx={{
                                borderRadius: "10px",
                                bgcolor: isReportLoading ? undefined : "#48CAE4",
                                color: isReportLoading ? undefined : "#000000",
                                textTransform: "none",
                                fontWeight: "normal",
                                "&:hover": { bgcolor: "#3ab3cc" },
                                "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.26)" },
                            }}
                        >
                            {isReportLoading ? "Generating report..." : "Print Report"}
                        </Button>
                    </Box>
                )}
            </Box>

            {isMarDialogOpen && selectedResident && (
                <Dialog
                    open={isMarDialogOpen}
                    onClose={() => {
                        setIsMarDialogOpen(false);
                        fetchMedicationData();
                    }}
                    maxWidth="xl"
                    fullWidth
                >
                    <MedicationAdministrationRecord
                        residentId={selectedResident.id!}
                        onClose={() => {
                            setIsMarDialogOpen(false);
                            fetchMedicationData();
                        }}
                        onSaveSuccess={() => fetchMedicationData()}
                        refreshTrigger={marRefreshTrigger}
                        medicationDefinitions={medications}
                        readOnly={(() => {
                            const rawStat = selectedResident.status || (selectedResident as any).residentStatus || (selectedResident as any).statusText || ((selectedResident as any).isActive ? "ACTIVE" : "INACTIVE");
                            return String(rawStat).toUpperCase().replace(/ /g, "_") !== "ACTIVE";
                        })()}
                    />
                </Dialog>
            )}

            {/* ── Controls Bar ── */}
            <Box sx={{ bgcolor: COLORS.surface, borderRadius: "16px", border: `1px solid ${COLORS.border}`, p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} className="no-print">
                <Autocomplete
                    options={residents.filter(r => r?.status?.toUpperCase() === "ACTIVE")}
                    getOptionLabel={o => `${(o as any).residentName || `${o.firstName} ${o.lastName}`} ${o.roomNumber ? `(Room: ${o.roomNumber}${o.bedNumber ? `, Bed: ${o.bedNumber}` : ""})` : ""}`}
                    value={selectedResident}
                    onChange={(_, v) => setSelectedResident(v)}
                    renderInput={params => <TextField {...params} label="Search Resident" variant="outlined" />}
                    sx={{ flex: 1, minWidth: 250 }}
                />

                {/* View Tabs */}
                <Box sx={{ display: "flex", bgcolor: COLORS.background, borderRadius: "10px", p: 0.5, gap: 0.5 }}>
                    {["Day", "Week", "Month"].map((t, idx) => (
                        <Button key={t} size="small" onClick={() => setActiveTab(idx)}
                            sx={{
                                borderRadius: "8px", textTransform: "none", fontWeight: "normal", px: 2, minWidth: 64,
                                bgcolor: activeTab === idx ? "#48CAE4" : "transparent",
                                color: activeTab === idx ? "#000000" : COLORS.textSecondary,
                                "&:hover": { bgcolor: activeTab === idx ? "#48CAE4" : "rgba(72,202,228,0.08)" },
                            }}>
                            {t}
                        </Button>
                    ))}
                </Box>

                {/* Date Navigator */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: COLORS.background, borderRadius: "10px", px: 1, py: 0.5 }}>
                    <IconButton size="small" onClick={handlePrevDate} sx={{ color: COLORS.textSecondary }}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" fontWeight={500} sx={{ color: COLORS.textPrimary, minWidth: 140, textAlign: "center" }}>
                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle", color: COLORS.primary }} />
                        {activeTab === 0 ? format(currentDate, "MMM d, yyyy")
                            : activeTab === 1 ? `${format(startOfWeek(currentDate), "MMM d")} – ${format(endOfWeek(currentDate), "MMM d")}`
                                : format(currentDate, "MMMM yyyy")}
                    </Typography>
                    <IconButton size="small" onClick={handleNextDate} disabled={isNextDisabled()} sx={{ color: isNextDisabled() ? COLORS.border : COLORS.textSecondary }}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {selectedResident && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }} className="no-print">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.5, borderRadius: "20px", bgcolor: cfg.bg, border: `1px solid ${cfg.color}25` }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: cfg.color }} />
                            <Typography variant="caption" fontWeight={600} sx={{ color: cfg.color, fontSize: "0.72rem" }}>{cfg.label}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {/* ── Stats ── */}
            {selectedResident && renderStatsRow()}

            {/* ── Calendar Content ── */}
            <Box sx={{ bgcolor: COLORS.surface, borderRadius: "16px", border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 12 }}>
                        <CircularProgress sx={{ color: COLORS.primary }} />
                    </Box>
                ) : (
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                        {!selectedResident ? renderEmptyState() : (
                            <>
                                {activeTab === 0 && renderTodayView()}
                                {activeTab === 1 && renderWeeklyView()}
                                {activeTab === 2 && renderMonthlyView()}
                            </>
                        )}
                    </Box>
                )}
            </Box>

            {/* ── Status Update Dialog ── */}
            <Dialog
                open={isUpdateDialogOpen}
                onClose={() => !isSubmitting && setIsUpdateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: '#48CAE4',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckCircleIcon />
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            Record Administration
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setIsUpdateDialogOpen(false)}
                        size="small"
                        sx={{ color: 'white' }}
                        disabled={isSubmitting}
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {selectedDateForUpdate && (
                        <Box sx={{ p: 3 }}>
                            {formError && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontWeight: 'bold' }}>
                                    {formError}
                                </Alert>
                            )}
                            {/* Medication Info Card */}
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                    <Box sx={{ p: 1, bgcolor: 'rgba(72,202,228,0.1)', borderRadius: 1.5, color: '#48CAE4' }}>
                                        <PharmacyIcon />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold" color="#1e293b" sx={{ lineHeight: 1.2, mb: 0.5, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                                            {updateForm.medicationName}
                                            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#64748b' }}>
                                                | Caregiver: {user?.name || user?.username || 'N/A'}
                                            </Typography>
                                        </Typography>
                                        <Typography variant="body2" color="#64748b" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <InfoIcon sx={{ fontSize: 14 }} /> {updateForm.dosage || 'N/A'} {selectedMed?.schedule ? `• ${selectedMed.schedule}` : (selectedMed?.frequency ? `• ${selectedMed.frequency}` : '')}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px dashed #e2e8f0', pt: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CalendarIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                        <Typography variant="body2" fontWeight="medium" color="#334155">
                                            {format(selectedDateForUpdate, 'EEEE, MMM do')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TimeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <TimePicker
                                                ampm={true}
                                                value={updateForm.time ? parse(updateForm.time, "HH:mm", new Date()) : null}
                                                onChange={(v) => {
                                                    if (v) {
                                                        setUpdateForm(p => ({ ...p, time: format(v, "HH:mm") }));
                                                    } else {
                                                        setUpdateForm(p => ({ ...p, time: "" }));
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        sx: {
                                                            width: 130,
                                                            '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' },
                                                            '& fieldset': { border: 'none' },
                                                            bgcolor: '#f1f5f9',
                                                            borderRadius: 2
                                                        }
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 10 }}>
                                            <InventoryIcon sx={{ fontSize: 16, color: '#48CAE4' }} />
                                            <Typography variant="caption" fontWeight="bold" color="#48CAE4">
                                                Stock: {updateForm.updatedStock || "N/A"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>

                            <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 1.5, px: 0.5 }}>
                                Results / Response {
                                    (selectedDateForUpdate && (checkIsReasonMandatory(selectedDateForUpdate, selectedMed?.slotTime || updateForm.time, selectedMed?.dateTime) || updateForm.status === 'Unable' || updateForm.status === 'NotTaken' || updateForm.status === 'Missed')) && (
                                        <span style={{ color: '#DC3545' }}>*</span>
                                    )
                                }
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter any results or response notes from daily medication..."
                                value={updateForm.results}
                                onChange={(e) => setUpdateForm(p => ({ ...p, results: e.target.value }))}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        fontSize: '0.9rem',
                                        '&:hover fieldset': {
                                            borderColor: '#48CAE4',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#48CAE4',
                                        },
                                    }
                                }}
                            />

                            <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mb: 2, px: 0.5 }}>
                                Select Result Status
                            </Typography>

                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                                {[
                                    { label: 'Taken', value: 'Taken', color: '#28A745', icon: <CheckCircleIcon /> },
                                    { label: 'Missed', value: 'NotTaken', color: '#DC3545', icon: <CancelIcon /> },
                                    { label: 'Refused', value: 'Refused', color: '#f06292', icon: <BlockIcon /> },
                                    { label: 'Out of Stock', value: 'OutOfStock', color: '#1e293b', icon: <InventoryIcon /> },
                                    { label: 'Discontinued', value: 'Discontinued', color: '#795548', icon: <RemoveCircleIcon /> },
                                    { label: 'PRN', value: 'PRN', color: '#0077B6', icon: <MedicationIcon /> },
                                ].map((status) => {
                                    const currentStock = updateForm.updatedStock || getAvailableStock(updateForm.medicationName);
                                    const isStockAvailable = currentStock !== "N/A" && currentStock !== "" && Number(currentStock) > 0;

                                    return (
                                        <Button
                                            key={status.value}
                                            variant="outlined"
                                            onClick={() => {
                                                setUpdateForm(p => ({ ...p, status: status.value }));
                                                const isReasonReq = checkIsReasonMandatory(selectedDateForUpdate, selectedMed?.slotTime || updateForm.time, selectedMed?.dateTime);
                                                if ((isReasonReq || status.value === 'Unable' || status.value === 'NotTaken') && !updateForm.results.trim()) {
                                                    setFormError(isReasonReq ? "Reason is mandatory for late or past-date medication administration." : `Results / Response is required for ${status.label}.`);
                                                    return;
                                                }
                                                const isNarcoticMed = !!(
                                                    selectedMed?.isNarcotic ||
                                                    selectedMed?.isNorcatiik ||
                                                    selectedMed?.isNorcatick ||
                                                    selectedMed?.is_narcotic ||
                                                    medications.find(m => normalizeMedName(m.medicationName) === normalizeMedName(selectedMed?.medicationName))?.isNarcotic
                                                );

                                                if (status.value === 'Taken' && isNarcoticMed) {
                                                    // Reset witness state and open the narcotic verification dialog
                                                    setSelectedWitnessId('');
                                                    setWitnessSignatureData(null);
                                                    setNarcoticDialogOpen(true);
                                                } else {
                                                    handleUpdateStatus(status.value);
                                                }
                                            }}
                                            disabled={
                                                isSubmitting ||
                                                (status.label === "Out of Stock" && isStockAvailable)
                                            }
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                borderRadius: 2,
                                                border: `1.5px solid ${status.color}20`,
                                                color: status.color,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                textTransform: 'none',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: `${status.color}10`,
                                                    border: `1.5px solid ${status.color}`,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 12px ${status.color}15`,
                                                }
                                            }}
                                        >
                                            {status.icon}
                                            <Typography variant="body2" fontWeight="bold">
                                                {status.label}
                                            </Typography>
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {isSubmitting && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#48CAE4' }} />
                    </Box>
                )}
            </Dialog>

            {/* ── Missed Doses Dialog ── */}
            <Dialog open={isMissedDialogOpen} onClose={() => setIsMissedDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: "#DC2626", color: "white", display: "flex", alignItems: "center", gap: 1, pt: 1.25, pb: 0.5 }}>
                    <WarningIcon />
                    Missed Medications
                    <Typography variant="caption" sx={{ ml: "auto", opacity: 0.8 }}>
                        {missedDosesList.length} Missed {activeTab === 0 ? "Today" : activeTab === 1 ? "This Week" : "This Month"}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 2, pb: 2 }}>
                    {missedDosesList.length > 0 ? (
                        <Stack spacing={0.75}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1.5, mt: 0, mb: -0.25 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ color: "#DC2626", fontSize: '0.7rem' }}>Select All</Typography>
                                <Checkbox
                                    checked={selectedMissedDoses.length === missedDosesList.length && missedDosesList.length > 0}
                                    indeterminate={selectedMissedDoses.length > 0 && selectedMissedDoses.length < missedDosesList.length}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    size="small"
                                    sx={{
                                        p: 0,
                                        ml: 0.5,
                                        color: "#DC2626",
                                        '&.Mui-checked': { color: "#DC2626" },
                                        '&.MuiCheckbox-indeterminate': { color: "#DC2626" }
                                    }}
                                />
                            </Box>
                            {missedDosesList.map((dose, idx) => (
                                <Paper key={idx} variant="outlined" sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 1.5, borderColor: "#DC262640", bgcolor: "#FEF2F2" }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={800} color="#991B1B" sx={{ fontSize: '0.85rem' }}>{dose.medicationName}</Typography>
                                        <Typography variant="caption" sx={{ color: "#7F1D1D", display: "flex", gap: 1, fontSize: '0.7rem', flexWrap: 'wrap' }}>
                                            <span><TimeIcon sx={{ fontSize: 11, verticalAlign: "middle", mr: 0.25 }} />{dose.dateTime ? format(new Date(dose.dateTime), "hh:mm a - MMM d") : "N/A"}</span>
                                            <span>• {dose.doseAmount || "N/A"}</span>
                                            <span style={{ fontWeight: 600 }}>• Stock: {dose.updatedStock && dose.updatedStock !== "N/A" ? dose.updatedStock : getAvailableStock(dose.medicationName)}</span>
                                        </Typography>
                                    </Box>
                                    <Checkbox
                                        checked={selectedMissedDoses.includes(idx)}
                                        onChange={() => handleToggleMissedDose(idx)}
                                        size="small"
                                        sx={{
                                            p: 0,
                                            color: "#DC2626",
                                            '&.Mui-checked': { color: "#DC2626" }
                                        }}
                                    />
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: "#16A34A", mb: 1, opacity: 0.8 }} />
                            <Typography variant="subtitle1" fontWeight={700} color="textPrimary">No missed medications!</Typography>
                            <Typography variant="body2" color="textSecondary">All caught up for this period.</Typography>
                        </Box>
                    )}

                    {missedDosesList.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mt: 2, mb: 1, px: 0.5, fontSize: '0.8rem' }}>
                                Update Selected to Status:
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                {[
                                    { label: 'Taken', value: 'Taken', color: '#28A745', icon: <CheckCircleIcon fontSize="small" /> },
                                    { label: 'Missed', value: 'NotTaken', color: '#DC3545', icon: <CancelIcon fontSize="small" /> },
                                    { label: 'Upcoming', value: 'PENDING', color: '#7c4dff', icon: <HistoryIcon fontSize="small" /> },
                                    { label: 'Refused', value: 'Refused', color: '#f06292', icon: <BlockIcon fontSize="small" /> },
                                    { label: 'Out of Stock', value: 'OutOfStock', color: '#1e293b', icon: <InventoryIcon fontSize="small" /> },
                                    { label: 'Discontinued', value: 'Discontinued', color: '#795548', icon: <RemoveCircleIcon fontSize="small" /> },
                                    { label: 'PRN', value: 'PRN', color: '#0288d1', icon: <PharmacyIcon fontSize="small" /> },
                                ].map((status) => {
                                    let isDisabled = selectedMissedDoses.length === 0 || isSubmitting || status.label === 'Upcoming';
                                    if (status.value === 'Taken') {
                                        const hasOutOfStock = selectedMissedDoses.some(idx => {
                                            const d = missedDosesList[idx];
                                            const stockVal = d.updatedStock && d.updatedStock !== "N/A" ? d.updatedStock : getAvailableStock(d.medicationName);
                                            return stockVal !== "N/A" && stockVal !== "" && Number(stockVal) <= 0;
                                        });
                                        if (hasOutOfStock) isDisabled = true;
                                    }
                                    return (
                                        <Button
                                            key={status.value}
                                            variant="outlined"
                                            onClick={() => handleBatchUpdateStatus(status.value)}
                                            disabled={isDisabled}
                                            sx={{
                                                py: 1,
                                                px: 1,
                                                borderRadius: 2,
                                                border: `1.5px solid ${status.color}20`,
                                                color: status.color,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                                textTransform: 'none',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    bgcolor: `${status.color}10`,
                                                    border: `1.5px solid ${status.color}`,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 4px 12px ${status.color}15`,
                                                },
                                                '&.Mui-disabled': {
                                                    opacity: 0.5,
                                                    borderColor: 'rgba(0,0,0,0.1)'
                                                }
                                            }}
                                        >
                                            {status.icon}
                                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                                                {status.label}
                                            </Typography>
                                        </Button>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1, pt: 0.5, justifyContent: "flex-end" }}>
                    <Button
                        onClick={() => setIsMissedDialogOpen(false)}
                        sx={{
                            color: COLORS.textSecondary,
                            fontWeight: 700,
                            textTransform: "none",
                            px: 2.5,
                            py: 0.75,
                            fontSize: "0.9rem",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Refused Medications Dialog ── */}
            <Dialog open={isRefusedDialogOpen} onClose={() => setIsRefusedDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: "#7C3AED", color: "white", display: "flex", alignItems: "center", gap: 1, pt: 1.25, pb: 0.5 }}>
                    <BlockIcon />
                    Refused Medications
                    <Typography variant="caption" sx={{ ml: "auto", opacity: 0.8 }}>
                        {refusedDosesList.length} Refused {activeTab === 0 ? "Today" : activeTab === 1 ? "This Week" : "This Month"}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 2, pb: 2 }}>
                    {refusedDosesList.length > 0 ? (
                        <Stack spacing={0.75}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1.5, mt: 0, mb: -0.25 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ color: "#7C3AED", fontSize: '0.7rem' }}>Select All</Typography>
                                <Checkbox
                                    checked={selectedRefusedDoses.length === refusedDosesList.length && refusedDosesList.length > 0}
                                    indeterminate={selectedRefusedDoses.length > 0 && selectedRefusedDoses.length < refusedDosesList.length}
                                    onChange={(e) => {
                                        if (e.target.checked) setSelectedRefusedDoses(refusedDosesList.map((_, i) => i));
                                        else setSelectedRefusedDoses([]);
                                    }}
                                    size="small"
                                    sx={{
                                        p: 0,
                                        ml: 0.5,
                                        color: "#7C3AED",
                                        '&.Mui-checked': { color: "#7C3AED" },
                                        '&.MuiCheckbox-indeterminate': { color: "#7C3AED" }
                                    }}
                                />
                            </Box>
                            {refusedDosesList.map((dose, idx) => (
                                <Paper key={idx} variant="outlined" sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 1.5, borderColor: "#7C3AED40", bgcolor: "#F5F3FF" }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={800} color="#5B21B6" sx={{ fontSize: '0.85rem' }}>{dose.medicationName}</Typography>
                                        <Typography variant="caption" sx={{ color: "#4C1D95", display: "flex", gap: 1, fontSize: '0.7rem', flexWrap: 'wrap' }}>
                                            <span><TimeIcon sx={{ fontSize: 11, verticalAlign: "middle", mr: 0.25 }} />{dose.dateTime ? format(new Date(dose.dateTime), "hh:mm a - MMM d") : "N/A"}</span>
                                            <span>• {dose.doseAmount || "N/A"}</span>
                                            <span style={{ fontWeight: 600 }}>• Stock: {dose.updatedStock && dose.updatedStock !== "N/A" ? dose.updatedStock : getAvailableStock(dose.medicationName)}</span>
                                        </Typography>
                                    </Box>
                                    <Checkbox
                                        checked={selectedRefusedDoses.includes(idx)}
                                        onChange={() => {
                                            setSelectedRefusedDoses(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
                                        }}
                                        size="small"
                                        sx={{
                                            p: 0,
                                            color: "#7C3AED",
                                            '&.Mui-checked': { color: "#7C3AED" }
                                        }}
                                    />
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: "#16A34A", mb: 1, opacity: 0.8 }} />
                            <Typography variant="subtitle1" fontWeight={700} color="textPrimary">No refused medications!</Typography>
                            <Typography variant="body2" color="textSecondary">Excellent adherence for this period.</Typography>
                        </Box>
                    )}

                    {refusedDosesList.length > 0 && (
                        <>
                            <Typography variant="subtitle2" fontWeight="bold" color="#475569" sx={{ mt: 2, mb: 1, px: 0.5, fontSize: '0.8rem' }}>
                                Update Selected to Status:
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                {[
                                    { label: 'Taken', value: 'Taken', color: '#28A745', icon: <CheckCircleIcon fontSize="small" /> },
                                    { label: 'Missed', value: 'NotTaken', color: '#DC3545', icon: <CancelIcon fontSize="small" /> },
                                    { label: 'Upcoming', value: 'PENDING', color: '#7c4dff', icon: <HistoryIcon fontSize="small" /> },
                                    { label: 'Refused', value: 'Refused', color: '#f06292', icon: <BlockIcon fontSize="small" /> },
                                    { label: 'Out of Stock', value: 'OutOfStock', color: '#1e293b', icon: <InventoryIcon fontSize="small" /> },
                                    { label: 'Discontinued', value: 'Discontinued', color: '#795548', icon: <RemoveCircleIcon fontSize="small" /> },
                                    { label: 'PRN', value: 'PRN', color: '#0288d1', icon: <PharmacyIcon fontSize="small" /> },
                                ].map((status) => {
                                    let isDisabled = selectedRefusedDoses.length === 0 || isSubmitting || status.label === 'Upcoming';
                                    if (status.value === 'Taken') {
                                        const hasOutOfStock = selectedRefusedDoses.some(idx => {
                                            const d = refusedDosesList[idx];
                                            const stockVal = d.updatedStock && d.updatedStock !== "N/A" ? d.updatedStock : getAvailableStock(d.medicationName);
                                            return stockVal !== "N/A" && stockVal !== "" && Number(stockVal) <= 0;
                                        });
                                        if (hasOutOfStock) isDisabled = true;
                                    }
                                    return (
                                        <Button
                                            key={status.value}
                                            variant="outlined"
                                            onClick={() => handleBatchUpdateRefused(status.value)}
                                            disabled={isDisabled}
                                            sx={{
                                                py: 1, px: 1, borderRadius: 2, border: `1.5px solid ${status.color}20`, color: status.color,
                                                display: 'flex', flexDirection: 'column', gap: 0.5, textTransform: 'none', transition: 'all 0.2s',
                                                '&:hover': { bgcolor: `${status.color}10`, border: `1.5px solid ${status.color}`, transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${status.color}15` },
                                                '&.Mui-disabled': { opacity: 0.5, borderColor: 'rgba(0,0,0,0.1)' }
                                            }}
                                        >
                                            {status.icon}
                                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>{status.label}</Typography>
                                        </Button>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1, pt: 0.5, justifyContent: "flex-end" }}>
                    <Button onClick={() => setIsRefusedDialogOpen(false)} sx={{ color: COLORS.textSecondary, fontWeight: 700, textTransform: "none", px: 2.5, py: 0.75, fontSize: "0.9rem", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Day Details Dialog ── */}
            <Dialog
                open={isDayDetailsDialogOpen}
                onClose={() => setIsDayDetailsDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{
                    bgcolor: COLORS.primary,
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CalendarIcon />
                        <Typography variant="h6" fontWeight={800}>
                            {selectedDayForDetails ? format(selectedDayForDetails, "MMMM d, yyyy") : ""}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setIsDayDetailsDialogOpen(false)} sx={{ color: "white" }}>
                        <CancelIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, pt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="textSecondary" sx={{ mb: 2, px: 0.5 }}>
                        Scheduled Medications ({dosesForSelectedDay.length})
                    </Typography>
                    <Stack spacing={1.5}>
                        {dosesForSelectedDay.map((dose, idx) => (
                            <MedPill
                                key={idx}
                                dose={dose}
                                hideStock={true}
                                date={selectedDayForDetails!}
                            />
                        ))}
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* ── STAT Dose Dialog ── */}
            <Dialog
                open={isRuntimeMedDialogOpen}
                onClose={() => !isSubmitting && setIsRuntimeMedDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
            >
                <DialogTitle sx={{
                    bgcolor: '#48CAE4',
                    color: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <MedicationIcon />
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            STAT Dose
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => setIsRuntimeMedDialogOpen(false)}
                        size="small"
                        sx={{ color: '#000000' }}
                        disabled={isSubmitting}
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {formError && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontWeight: 'bold' }}>
                            {formError}
                        </Alert>
                    )}

                    <Grid container spacing={2.5}>
                        {/* Row 1: Medication Name, Dose, Unit, Route */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Medication Name"
                                value={runtimeMedForm.medicationName}
                                onChange={(e) => setRuntimeMedForm(p => ({ ...p, medicationName: e.target.value }))}
                                size="small"
                                required
                                InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                label="Dose"
                                value={
                                    runtimeMedForm.dosage
                                        ? `${runtimeMedForm.dosage}${runtimeMedForm.unit ? ` ${runtimeMedForm.unit}` : ""}`
                                        : ""
                                }
                                onChange={(e) => {
                                    const rawValue = e.target.value;
                                    const unitSuffix = runtimeMedForm.unit ? ` ${runtimeMedForm.unit}` : "";

                                    let numericValue = rawValue;
                                    if (
                                        unitSuffix &&
                                        rawValue
                                            .toLowerCase()
                                            .endsWith(unitSuffix.toLowerCase())
                                    ) {
                                        numericValue = rawValue.slice(
                                            0,
                                            rawValue.length - unitSuffix.length,
                                        );
                                    }

                                    numericValue = numericValue.replace(/\s+/g, "");

                                    if (/^[0-9./-]*$/.test(numericValue)) {
                                        setRuntimeMedForm((p) => ({
                                            ...p,
                                            dosage: numericValue,
                                        }));
                                    }
                                }}
                                size="small"
                                required
                                InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel sx={{ '& .MuiFormLabel-asterisk': { color: '#ef4444' } }}>Unit</InputLabel>
                                <Select
                                    label="Unit"
                                    value={runtimeMedForm.unit}
                                    onChange={(e) => setRuntimeMedForm(p => ({ ...p, unit: e.target.value }))}
                                >
                                    <MenuItem value="mg">mg</MenuItem>
                                    <MenuItem value="mcg">mcg</MenuItem>
                                    <MenuItem value="ml">ml</MenuItem>
                                    <MenuItem value="G">G</MenuItem>
                                    <MenuItem value="Units">Units</MenuItem>
                                    <MenuItem value="Puff">Puff</MenuItem>
                                    <MenuItem value="Drop">Drop</MenuItem>
                                    <MenuItem value="Tablet">Tablet</MenuItem>
                                    <MenuItem value="Capsule">Capsule</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel sx={{ '& .MuiFormLabel-asterisk': { color: '#ef4444' } }}>Route</InputLabel>
                                <Select
                                    label="Route"
                                    value={runtimeMedForm.route}
                                    onChange={(e) => setRuntimeMedForm(p => ({ ...p, route: e.target.value as any }))}
                                    MenuProps={{
                                        disableScrollLock: true,
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        PaperProps: { sx: { maxHeight: 300 } }
                                    }}
                                >
                                    <MenuItem value="">Select Route</MenuItem>
                                    {Array.isArray(routes) && routes.map((opt: any) => (
                                        <MenuItem key={opt.id} value={opt.valueName}>{opt.valueName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Row 2: Date, Given Time, Stock Quantity */}
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Date"
                                    format="dd/MM/yyyy"
                                    value={runtimeMedForm.startDate ? new Date(runtimeMedForm.startDate) : null}
                                    onChange={(v) => setRuntimeMedForm(p => ({ ...p, startDate: v }))}
                                    disabled
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            required: true,
                                            InputLabelProps: { sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } } }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <TimePicker
                                    label="Administration Time / Given Time"
                                    ampm={true}
                                    value={runtimeMedForm.givenTime ? parse(runtimeMedForm.givenTime, "HH:mm", new Date()) : null}
                                    onChange={(v) => {
                                        if (v) {
                                            setRuntimeMedForm(p => ({ ...p, givenTime: format(v, "HH:mm") }));
                                        } else {
                                            setRuntimeMedForm(p => ({ ...p, givenTime: "" }));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            required: true,
                                            InputLabelProps: {
                                                sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Add Stock Quantity"
                                value={runtimeMedForm.stockQuantity}
                                onChange={(e) => setRuntimeMedForm(p => ({ ...p, stockQuantity: e.target.value }))}
                                size="small"
                                placeholder="Optional"
                                disabled
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '& fieldset': { borderColor: '#cbd5e1' }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Row 3: Notes / Reason */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes / Reason"
                                value={runtimeMedForm.note}
                                onChange={(e) => setRuntimeMedForm(p => ({ ...p, note: e.target.value }))}
                                size="small"
                                placeholder="Enter any notes or special reasons (optional)..."
                                multiline
                                rows={2.5}
                            />
                        </Grid>



                        {/* Row 5: Actions */}
                        {!isOnlyCaregiver && (
                            <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleRuntimeMedicationSubmit}
                                    disabled={isSubmitting}
                                    sx={{
                                        bgcolor: '#48CAE4',
                                        color: '#000000',
                                        fontWeight: 'normal',
                                        px: 4,
                                        py: 1,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#3ab3cc' }
                                    }}
                                >
                                    {isSubmitting ? 'Adding...' : 'Add Medication'}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                {isSubmitting && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        bgcolor: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: COLORS.primary }} />
                    </Box>
                )}
            </Dialog>

            {/* Legacy SignatureInput — kept for direct open if ever needed */}
            <SignatureInput
                open={isSignatureInputOpen}
                onClose={() => setIsSignatureInputOpen(false)}
                onSave={async (signatureDataUrl) => {
                    const file = dataURLtoFile(signatureDataUrl, 'signature.png');
                    await handleUpdateStatus("Taken", file);
                }}
                title="Narcotic Signature Verification"
                confirmMessage="Confirm narcotic medication administration?"
            />

            {/* ── Narcotic Administration Verification Dialog ── */}
            <Dialog
                open={narcoticDialogOpen}
                onClose={() => setNarcoticDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#48CAE4', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicationIcon />
                    Narcotic Administration Verification
                    <IconButton onClick={() => {
                        setNarcoticDialogOpen(false);
                        setAdminSignatureData(null);
                        setAdminSignatureMode(null);
                        setBulkUpdatePendingStatus(null);
                        setBulkUpdateSource(null);
                    }} sx={{ ml: 'auto', color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>

                        {/* ── Column 1: Administering Caregiver Signature ── */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ border: '1px solid #e0f4fa', borderRadius: 2, p: 2, bgcolor: '#f9fdff', height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, color: '#0077B6' }}>
                                    Primary witness
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Signed as: <strong>{user?.name || user?.username}</strong>
                                </Typography>

                                {myProfileSignature ? (
                                    <Box sx={{ border: '2px solid #48CAE4', borderRadius: 2, p: 2, bgcolor: '#f0fbff', mb: 2, textAlign: 'center' }}>
                                        <img
                                            src={myProfileSignature}
                                            alt="My Signature"
                                            style={{ maxHeight: 100, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                                        />
                                        <Typography variant="caption" color="primary" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                                            Using Profile Signature
                                        </Typography>
                                    </Box>
                                ) : adminSignatureData ? (
                                    <Box sx={{ border: '2px dashed #48CAE4', borderRadius: 2, p: 2, bgcolor: '#f0fbff', mb: 2, textAlign: 'center' }}>
                                        <img
                                            src={adminSignatureData}
                                            alt="Admin Signature"
                                            style={{ maxHeight: 100, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                                        />
                                        <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                                            ✓ Signature captured for this administration
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                            onClick={() => setMySignatureDialogOpen(true)}
                                        >
                                            Re-sign
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                ref={adminUploadRef}
                                                onChange={(e) => handleFileUpload(e, setAdminSignatureData)}
                                            />
                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth
                                                startIcon={<CloudUploadIcon />}
                                                sx={{
                                                    bgcolor: "#48CAE4",
                                                    color: "#000000",
                                                    fontWeight: "normal",
                                                    "&:hover": { bgcolor: "#3ab3cc" }
                                                }}
                                                onClick={() => {
                                                    setAdminSignatureMode('upload');
                                                    adminUploadRef.current?.click();
                                                }}
                                            >
                                                Upload Signature
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                fullWidth
                                                startIcon={<EditIcon />}
                                                sx={{
                                                    bgcolor: "#48CAE4",
                                                    color: "#000000",
                                                    fontWeight: "normal",
                                                    "&:hover": { bgcolor: "#3ab3cc" }
                                                }}
                                                onClick={() => {
                                                    setAdminSignatureMode('digital');
                                                    setMySignatureDialogOpen(true);
                                                }}
                                            >
                                                Digital Signature
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* ── Column 2: Witness Caregiver ── */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ border: '1px solid #e0f4fa', borderRadius: 2, p: 2, bgcolor: '#f9fdff', height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5, color: '#0077B6' }}>
                                    Secondary Witness
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Select another caregiver to witness this administration. They will provide their own signature.
                                </Typography>

                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Select Witness Caregiver</InputLabel>
                                    <Select
                                        value={selectedWitnessId}
                                        label="Select Witness Caregiver"
                                        disabled={caregiversLoading}
                                        onChange={(e) => {
                                            setSelectedWitnessId(e.target.value);
                                            setWitnessSignatureData(null);
                                            setWitnessSignatureMode(null);
                                        }}
                                        endAdornment={
                                            caregiversLoading ? (
                                                <CircularProgress size={18} sx={{ mr: 2 }} />
                                            ) : undefined
                                        }
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {caregiversLoading ? (
                                            <MenuItem disabled>
                                                <em>Loading caregivers...</em>
                                            </MenuItem>
                                        ) : otherCaregivers.length === 0 ? (
                                            <MenuItem disabled>
                                                <em>No other caregivers available</em>
                                            </MenuItem>
                                        ) : (
                                            otherCaregivers.map((c) => (
                                                <MenuItem key={c.id || c.keycloakId} value={c.id || c.keycloakId || ''}>
                                                    {c.firstName} {c.lastName}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>

                                {selectedWitnessId && (
                                    <Box>
                                        {witnessSignatureData && (
                                            <Box sx={{ border: '1px solid #c8e6c9', borderRadius: 1.5, p: 1.5, bgcolor: 'white', mb: 2, textAlign: 'center' }}>
                                                <img
                                                    src={witnessSignatureData}
                                                    alt="Witness Signature"
                                                    style={{ maxHeight: 80, maxWidth: '100%', display: 'block', margin: '0 auto' }}
                                                />
                                                <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mt: 0.5, display: 'block' }}>
                                                    ✓ Witness signature captured
                                                </Typography>
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                ref={witnessUploadRef}
                                                onChange={(e) => handleFileUpload(e, setWitnessSignatureData)}
                                            />
                                            {witnessSignatureData ? (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<RemoveCircleIcon sx={{ color: '#d32f2f' }} />}
                                                    sx={{
                                                        color: '#d32f2f',
                                                        fontWeight: 500,
                                                        alignSelf: 'flex-start',
                                                        textTransform: 'none',
                                                        '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' }
                                                    }}
                                                    onClick={() => {
                                                        setWitnessSignatureData(null);
                                                        setWitnessSignatureMode(null);
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        startIcon={<CloudUploadIcon />}
                                                        sx={{
                                                            bgcolor: "#48CAE4",
                                                            color: "#000000",
                                                            fontWeight: "normal",
                                                            "&:hover": { bgcolor: "#3ab3cc" }
                                                        }}
                                                        onClick={() => {
                                                            setWitnessSignatureMode('upload');
                                                            witnessUploadRef.current?.click();
                                                        }}
                                                    >
                                                        Upload Signature
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        startIcon={<EditIcon />}
                                                        sx={{
                                                            bgcolor: "#48CAE4",
                                                            color: "#000000",
                                                            fontWeight: "normal",
                                                            "&:hover": { bgcolor: "#3ab3cc" }
                                                        }}
                                                        onClick={() => {
                                                            setWitnessSignatureMode('digital');
                                                            setWitnessSignatureDialogOpen(true);
                                                        }}
                                                    >
                                                        Digital Signature
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => {
                        setNarcoticDialogOpen(false);
                        setAdminSignatureData(null);
                        setAdminSignatureMode(null);
                        setWitnessSignatureData(null);
                        setWitnessSignatureMode(null);
                        setSelectedWitnessId('');
                        setBulkUpdatePendingStatus(null);
                        setBulkUpdateSource(null);
                    }} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={
                            (!myProfileSignature && !adminSignatureData) ||
                            (!!selectedWitnessId && !witnessSignatureData) ||
                            isSubmitting
                        }
                        onClick={async () => {
                            const effectiveAdminSignature = myProfileSignature || adminSignatureData;
                            if (!effectiveAdminSignature) return;
                            if (selectedWitnessId && !witnessSignatureData) return;

                            const file = dataURLtoFile(effectiveAdminSignature, 'signature.png');
                            // In a real scenario, we'd also process the witnessSignatureData here if needed

                            setNarcoticDialogOpen(false);
                            setAdminSignatureData(null);
                            setAdminSignatureMode(null);
                            setWitnessSignatureData(null);
                            setWitnessSignatureMode(null);
                            setSelectedWitnessId('');

                            if (bulkUpdateSource === 'Missed' && bulkUpdatePendingStatus) {
                                await handleBatchUpdateStatus(bulkUpdatePendingStatus, effectiveAdminSignature);
                            } else if (bulkUpdateSource === 'Refused' && bulkUpdatePendingStatus) {
                                await handleBatchUpdateRefused(bulkUpdatePendingStatus, effectiveAdminSignature);
                            } else {
                                await handleUpdateStatus('Taken', file);
                            }
                            setBulkUpdateSource(null);
                            setBulkUpdatePendingStatus(null);
                        }}
                        sx={{ bgcolor: '#28A745', '&:hover': { bgcolor: '#218838' } }}
                    >
                        {isSubmitting
                            ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />
                            : <CheckCircleIcon sx={{ mr: 0.5, fontSize: 18 }} />}
                        Confirm Administration
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Admin Caregiver — inline signature creation (when no profile signature) */}
            <SignatureInput
                open={mySignatureDialogOpen}
                onClose={() => setMySignatureDialogOpen(false)}
                onSave={(dataUrl) => {
                    setAdminSignatureData(dataUrl);
                    setMySignatureDialogOpen(false);
                }}
                title="Create Your Digital Signature"
                confirmMessage="Confirm your signature for this narcotic administration?"
                initialTab={0}
                hideTabs={true}
            />

            {/* Witness SignatureInput dialog (handles both Upload and Digital draw tabs) */}
            <SignatureInput
                open={witnessSignatureDialogOpen}
                onClose={() => setWitnessSignatureDialogOpen(false)}
                onSave={(dataUrl) => {
                    setWitnessSignatureData(dataUrl);
                    setWitnessSignatureDialogOpen(false);
                }}
                title="Witness Signature"
                confirmMessage="Confirm your witness signature for this narcotic administration?"
                initialTab={0}
                hideTabs={true}
            />

        </Box>
    );
};

export default MedicationSchedulePage;